import * as THREE from 'three';
import { createRenderContext } from './render/scene.js';
import { createCueSpinWidget, type CueSpinWidget } from './render/cue-spin-widget.js';
import { buildTable } from './render/table.js';
import { addTableMarks } from './render/table-marks.js';
import { addTableRails, railOuterX, railOuterY } from './render/table-rails.js';
import { addTablePockets } from './render/table-pockets.js';
import { addTableCushions } from './render/table-cushions.js';
import { addTableCabinet } from './render/table-cabinet.js';
import { setFeltColor, loadFeltColor } from './render/theme.js';
import {
  createBallViews,
  syncBallViews,
  disposeBallViews,
  updateHaloPulse,
} from './render/balls.js';
import { createCameraRig } from './render/camera-rig.js';
import { Cue } from './render/vendor/cue.js';
import { createControls } from './ui/controls.js';
import { chatTts } from './audio/chat-tts.js';
import { createBreakRunTracker } from './ui/break-run.js';
import { createInventoryStore, getItemDefinition, allItemDefinitions } from './inventory/state.js';
import type { ItemEffects } from './inventory/types.js';
import type { InventorySnapshot } from './net/lobby.js';
import { applyCueShaftAppearance } from './render/cue-customization.js';
import { createShotGuide } from './render/shot-guide.js';
import { createReflectGuide } from './render/reflect-guide.js';
import { createCutAngleFrame } from './render/cut-angle-frame.js';
import { createPocketGlows } from './render/pocket-glows.js';
import { createDragGuide } from './render/drag-guide.js';
import { AIM_GRAB_PX_EXPORT, AIM_FORWARD_LEN_M } from './ui/controls.js';
import { createCueGlimmer } from './render/cue-glimmer.js';
import { createSoundEngine } from './audio/sound.js';
import {
  isAutoTopAfterShot,
  isAutoTopOnObscured,
  getPlayMode,
  isPowerGuide,
  isReflectGuide,
  getHumidity,
  getSquirt,
  isSoundEnabled,
  isBreakSpread,
  isThreePointBreak,
  applyExternalHumidity,
  setMatchActive,
  isCutAngleGuide,
  isShotFreezeCam,
  SHOT_FREEZE_CAM_ZOOM_FACTOR,
  isDragGuideEnabled,
  isBroadcastOn,
  getBroadcastMode,
  subscribeBroadcastModeChange,
  isBroadcastSpectatorDefault,
  setBroadcastModeSessionOverride,
  isBroadcastBarVisible,
  subscribeBroadcastBarVisibilityChange,
} from './ui/settings.js';
import {
  BROADCAST_PRESETS,
  findBroadcastPresetByKey,
  type BroadcastPreset,
  type BroadcastPresetId,
  type SceneContext,
} from './render/broadcast-presets.js';
import {
  initDirectorState,
  reduceDirector,
  applyDirectorEffects,
  type DirectorAction,
  type DirectorState,
} from './render/broadcast-director.js';
// 注: BroadcastPresetId は Map のキー型として使用（型のみ参照）。
// DirectorState/Action は Director 配線（dispatchDirector / per-frame tick）で使う。
import {
  pointsInFrustum,
  fallbackTargetPocket,
  isClearlyMissingPocket,
  computePostFreezeFraming,
} from './sim/shot-freeze-cam.js';
// 注: getPlayMode は上で既に import 済み（L21）。重複import を避ける。
import {
  type PlayMode,
  ballRadius,
  getTableLayout,
  createWorld,
  resetWorld,
  step,
  isResting,
  snapshot,
  type PathPoint,
  shotPrediction,
  firstContactDistance,
  aimedTargetId,
  followFrame,
  simulateCuePath,
  applyTableCondition,
  forceSettle,
  restActivity,
  dropPottedBalls,
  lowestOnTableBallId,
  ballPositionById,
  respotCueBall,
  isCueBallScratched,
  cueBallSpeed,
  objectBallsRemaining,
  serializeBalls,
  restoreBalls,
  drainShotOutcomes,
  beginShotObservation,
  endShotObservation,
  objectBallsBehindHeadString,
  detectDoubleHitGeometry,
  spotBall,
  placeBallAt,
  addBall,
  removeBall,
  shoot,
  applySquirt,
  setRackSpread,
  onTablePositions,
  applyPlaybackFrame,
  forcePotBalls,
  getPocketAims,
  type ShotOutcome,
  type PreShot,
} from './sim/world.js';
import { loadSession, saveSession } from './state/session.js';
import { registerPersistHooks } from './state/lifecycle.js';
import { readJson, writeJson } from './state/storage.js';
import { initRuleState, dispatch } from './sim/rules/nineball.js';
import type {
  RuleState,
  RuleParams,
  ShotResult,
  FoulReason,
  PlayerId,
  Phase,
  ShotObservation,
} from './sim/rules/types.js';
import {
  beginAnalytics,
  finalizeAnalytics,
  type AnalyticsContext,
} from './sim/analytics.js';
import {
  resolveDrill,
  stepDrill,
  drillMaxShots,
  initDrillRun,
  DRILLS,
  DRILL_RANKS,
  type DrillLayout,
  type DrillRunState,
} from './sim/drill-layouts.js';
import {
  loadDrillProgress,
  saveDrillProgress,
  recordDrillClear,
  mergeDrillProgress,
  type DrillProgress,
} from './state/drill-progress.js';
import { createDrillPanel, type DrillPanelApi } from './ui/drill-panel.js';
import { createTransport, createLobby, isRemoteNet } from './net/net-config.js';
import { createWakeLockController } from './net/wake-lock.js';
import type { Transport, NetMessage, ShotPayload } from './net/transport.js';
import {
  createTrackRecorder,
  createTrackPlayer,
  TRACK_BUFFER_MS,
  type TrackRecorder,
  type TrackPlayer,
} from './net/shot-track.js';
import type {
  Roster,
  MatchAssignment,
  LobbyUser,
  LeaderboardEntry,
  AuthResult,
  ShotRecord,
  RaceConfig,
  RaceProposal,
  RaceUpdated,
  RaceStartInfo,
  RaceAborted,
  PickFailReason,
} from './net/lobby.js';
import type { LobbyRowView } from './ui/controls.js';
import {
  initRaceState,
  rackEnded,
  type RaceState,
  type RaceTargetWins,
  type BreakRule,
} from './ai/race.js';
import {
  planShot as cpuPlanShot,
  planBallInHand as cpuPlanBih,
  planPushOutShot as cpuPlanPushOut,
  hasClearPotOpportunity as cpuHasClearPot,
  isCpuUserId,
  getCpuPlayer,
  getCpuPlayers,
  type CpuPlayer,
} from './ai/cpu-players.js';

const canvas = document.getElementById('app');
if (!(canvas instanceof HTMLCanvasElement)) {
  throw new Error('canvas#app が見つかりません');
}

// 効果音エンジン（自前・GPL非依存）。AudioContext は自動再生制限のため
// 最初のユーザー操作で resume する（それまでは無音）。
// ★キャプチャフェーズで登録する: パワートラック/撞点パッド等の pointerdown は
//   stopPropagation するため、バブリングだと window まで届かず解放されない。
//   キャプチャなら stopPropagation より先に必ず発火する。複数イベントでカバー。
const sound = createSoundEngine();
const unlockAudio = (): void => sound.resume();
const unlockOpts: AddEventListenerOptions = { capture: true, passive: true };
window.addEventListener('pointerdown', unlockAudio, unlockOpts);
window.addEventListener('touchstart', unlockAudio, unlockOpts);
window.addEventListener('keydown', unlockAudio, unlockOpts);

// 連続ブレイクランアウト記録（勲章バッジ＋localStorage 永続）。
const breakRun = createBreakRunTracker();

// インベントリ（保有・装備）のローカルストア。サーバーからの inventoryUpdate を反映する受け皿。
// 未ログイン時は空のまま。ログイン成功 or 取得時に setAdmin/apply で反映される。
const inventory = createInventoryStore();
// 装備変更があるたびにキュー外観を再適用（3D mesh + ショットUI キュー画像）。
// 起動時は cue がまだ初期化されていない可能性があるため、cue 初期化完了後に最初の適用を行う（後述）。
// インベントリパネルの再描画もこの subscribe で行う（取得/装備変更を即時に画面反映）。
// きらめき色も装備に応じて切替（黒シャフトに暖色 emissive が乗ると木製に見える現象の回避）。
inventory.subscribe(() => {
  // 自分の装備変更 → アクティブ側が「自分」なら即時反映、相手手番中は次の自分手番でキャッシュ反映。
  // applyActiveCueAppearance 内で ruleState.current/myPlayer に基づき適切な effects を選択する。
  applyActiveCueAppearance();
  refreshInventoryPanel();
});
// ブレイクランアウト成立時のフック: バナー表示 + race 中なら自分のカウントを加算。
// Perfect Game 判定は race 終了タイミングで maybeRecordResult から行う（ここでは加算のみ）。
breakRun.setOnBreakRunout(() => {
  controlsApi.showMatchEvent('Break and run out!', '', 'win');
  // race 中で自分が撞いた直後のクリア時のみ加算（CPU/相手の撞きは下記 recordShot 経路で除外）。
  if (currentRace) {
    raceBreakRunByMe += 1;
  }
});

// Perfect Game 用花火演出（自前 DOM + CSS keyframes・外部依存なし）。
// 画面上に複数の打上花火パーティクルを生成し、5秒後に自動削除する。
const fireFireworks = (): void => {
  const root = document.createElement('div');
  root.className = 'pool-fireworks';
  document.body.appendChild(root);

  const W = window.innerWidth;
  const H = window.innerHeight;
  // 打上げる花火の数（位置をずらして時間差で発火）。
  const BURST_COUNT = 6;
  // 1花火あたりのパーティクル数。
  const PARTICLES_PER_BURST = 28;

  for (let burst = 0; burst < BURST_COUNT; burst++) {
    // 各 burst の中心は画面上半分のランダム位置。delay でずらす（決定的に分散）。
    const cx = W * (0.15 + 0.7 * ((burst * 73 + 11) % 100) / 100);
    const cy = H * (0.18 + 0.4 * ((burst * 41 + 7) % 100) / 100);
    const delayMs = burst * 320;
    const hueBase = (burst * 67) % 360;

    window.setTimeout(() => {
      for (let i = 0; i < PARTICLES_PER_BURST; i++) {
        const angle = (i / PARTICLES_PER_BURST) * Math.PI * 2;
        const radius = 70 + ((i * 17) % 50); // 70〜120 px
        const dx = Math.cos(angle) * radius;
        const dy = Math.sin(angle) * radius;
        const hue = (hueBase + (i * 13) % 60) % 360;
        const fw = document.createElement('div');
        fw.className = 'pool-firework';
        fw.style.left = `${cx}px`;
        fw.style.top = `${cy}px`;
        fw.style.setProperty('--dx', `${dx}px`);
        fw.style.setProperty('--dy', `${dy}px`);
        fw.style.setProperty('--hue', String(hue));
        root.appendChild(fw);
      }
    }, delayMs);
  }

  // 5秒後にコンテナごと削除（個別アニメは1.4秒で完了、最後の burst+animation 完了を待つ）。
  window.setTimeout(() => {
    if (root.parentNode) root.parentNode.removeChild(root);
  }, BURST_COUNT * 320 + 1800);
};

const ctx = createRenderContext(canvas);
// 統合3Dショットウィジェット Spike A（描画核検証・非侵襲）。
// `?cuewidget=1` または localStorage `pool.spike.cueWidget`='1' のときのみ有効化。
// 設計: knowledge/architecture/unified-shot-widget.md
const cueSpinWidgetEnabled = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cuewidget') === '1') return true;
    return localStorage.getItem('pool.spike.cueWidget') === '1';
  } catch {
    return false;
  }
};
const cueSpinWidget: CueSpinWidget | null = cueSpinWidgetEnabled()
  ? createCueSpinWidget(ctx.scene.environment)
  : null;
// Spike 中は既存の撞点/仰角/縦キューHUDを退避（CSS で非表示）し、新ウィジェットに場所を譲る。
if (cueSpinWidget) document.body.classList.add('pool-cuewidget-on');
buildTable(ctx.scene);
addTablePockets(ctx.scene);
addTableCushions(ctx.scene);
addTableRails(ctx.scene);
addTableCabinet(ctx.scene);
addTableMarks(ctx.scene);

// 全フェルトマテリアル登録後に、保存済みのラシャ色を適用する。
setFeltColor(loadFeltColor());

// 保存済みのテーブルコンディション（湿度→摩擦）をエンジンへ反映。
applyTableCondition(getHumidity());

// 保存済みセッション（前回の進行）があれば、そのモードで盤面を作って復元する。
// version 不一致・破損は loadSession 側で黙って破棄され null になる（③A）。
//
// セッション保存モードと設定 UI（pool.playMode）の不整合を救済する:
//   旧バージョン（〜2026-06-16）はドリル中も persist() を呼んでいたため、
//   ドリル進入後にタブを閉じた既存ユーザーは savedSession.mode='practice' で
//   保存される一方、設定 UI は元のモード（例: 9ボール）を保持していた。
//   起動時にこの不一致を検知したら、設定 UI 側のモードを採用して再ラックする
//   （savedSession を破棄せず balls だけ無視→resumed=false でデフォルト盤面）。
const savedSession = loadSession();
const settingsMode = getPlayMode();
const sessionModeMismatched = savedSession !== null && savedSession.mode !== settingsMode;
let playMode: PlayMode = savedSession && !sessionModeMismatched ? savedSession.mode : settingsMode;
const table = createWorld(playMode);
// 復元成功時のみ resumed=true。id 構成が一致しなければ新規ラックのまま続行。
// モード不一致時は球配置の復元を意図的にスキップ（設定 UI のモードでデフォルト盤面を見せる）。
const resumed =
  savedSession !== null && !sessionModeMismatched && restoreBalls(table, savedSession.balls);
let views = createBallViews(snapshot(table), ctx.scene);
if (!views.get(0)) throw new Error('手球メッシュが見つかりません');

// ---- 9ボール ルールエンジン（対戦・ローカルはホットシート） ----
// ルールは nineBall モードでのみ有効。物理→観測（ShotObservation）→純粋reducer。
const RULES_MODE: PlayMode = 'nineBall';
let ruleParams: RuleParams = {
  doubleHit: {
    enabled: true, // ダブルヒット近接球プロキシを有効化（ハウスルール・§8.1）
    gapMaxM: 0.005,
    elevationMaxRad: Math.PI / 6,
    cutAngleMaxRad: (70 * Math.PI) / 180,
  },
  online: false, // 接続で true に切り替える（オンライン2P）。
  breakThreePoint: isThreePointBreak(), // 3ポイントルール（設定・既定OFF）
};
let rulesActive = playMode === RULES_MODE;
let ruleState: RuleState = initRuleState({ breaker: 0 });
let lastRuleResult: ShotResult | null = null;
let shotOutcomes: ShotOutcome[] = []; // ショット中に蓄積する id 付きイベント
let preShot: PreShot | null = null; // 打前スナップショット（観測の基準）
let pendingDoubleHit = false; // 打前に確定した二度撞きプロキシ結果
// ブレイク中にヘッドストリングを越えた的玉 id（3ポイント判定用・越えて戻った球も保持）。
const breakHeadStringIds = new Set<number>();

// ---- ソロ・ドリル（F-30） ----
// 進行中ドリル（プラクティス基盤で動作・null=通常プレイ）。ショット上限制（drill-pass-model）。
let currentDrill: { layout: DrillLayout; run: DrillRunState; maxShots: number } | null = null;
// ドリルに入る直前のプレイモード（終了時にここへ戻す）。null=ドリル外。
let drillReturnMode: PlayMode | null = null;
let drillProgress: DrillProgress = loadDrillProgress();
let drillPanel: DrillPanelApi | null = null;

// ---- 通信対戦（方式B: 権威者スナップショット・ループバック検証） ----
let transport: Transport | null = null;
let myPlayer: PlayerId = 0; // host=0 / guest=1
let netConnected = false;
let spectating = false; // 観戦中（入力全ロック・送信しない）
let spectateMatchId: string | null = null; // 観戦中の試合 id（ロスター照合で実名表示）
let replayingRemote = false; // 相手ショットのローカル再生中（静止時に権威で補正）
let awaitingAuthoritative = false; // 再生が静止したが shotEnd 未着＝結果待ち
let pendingAuthoritative: { state: RuleState; balls: ReturnType<typeof serializeBalls> } | null =
  null;
let preShotSnapshot: ReturnType<typeof serializeBalls> | null = null; // 撞く直前の全球（送信用）
// ---- 遠隔ショットの軌跡ストリーミング（端末間の物理非決定性を回避） ----
// 送信側（権威）: 物理計算中に球位置をサンプリングして track でライブ送信。
let trackRecorder: TrackRecorder | null = null;
let shotPlayMs = 0; // 送信側: ショット開始からの経過ms（サンプルのタイムスタンプ）
// 受信側（遠隔）: 物理再生せず軌跡を補間再生する。
let remotePlayActive = false;
let remotePlayStartMs = 0; // 受信側: 再生時計の基準（wallMs）
let trackPlayer: TrackPlayer | null = null;
let wallMs = 0; // animate で毎フレーム加算する単調増加クロック（再生タイミング用）
let lastAimSig = ''; // 照準同期の重複送信抑止
let lastAimSentMs = 0; // 照準同期のスロットル
// 相手・観戦のニックネーム（HUD 表示用）。
let opponentNickname = '';
// 切断検出: 相手の最終受信時刻と接続状態。一定無通信で reconnecting → 15s 後に disconnected。
let lastPeerActivity = 0;
let netState: 'connected' | 'reconnecting' | 'disconnected' = 'connected';
let matchHbTimer = 0; // ハートビート送信
let matchMonTimer = 0; // 切断監視
const RECONNECT_AFTER_MS = 5000;
const DISCONNECT_AFTER_MS = 5000 + 15000; // 5s で再接続中表示→さらに15sで切断確定
// 入力ロック: 観戦中／自分の手番でない（相手のショット/配置を待つ）。
const isInputLocked = (): boolean =>
  netConnected && (spectating || ruleState.current !== myPlayer);

// ---- ロビー（在席・自動マッチメイク・観戦・ニックネーム） ----
const NICK_KEY = 'pool.nickname';
const loadNickname = (): string => {
  try {
    return localStorage.getItem(NICK_KEY) ?? '';
  } catch {
    return '';
  }
};
const saveNickname = (name: string): void => {
  try {
    localStorage.setItem(NICK_KEY, name);
  } catch (err) {
    console.error('nickname保存失敗:', err);
  }
};
let nickname = loadNickname();
let amWaiting = false;
let lastRoster: Roster = [];
// 順位リスト用の永続リーダーボード（サーバー権威・オフライン含む全件）のキャッシュ。
let leaderboard: readonly LeaderboardEntry[] = [];
const lobby = createLobby();
// 最新の順位表を取得して再描画する（ロビーを開いた時・join 時）。失敗時はキャッシュ据え置き。
const refreshLeaderboard = (): void => {
  void (async (): Promise<void> => {
    try {
      leaderboard = await lobby.requestLeaderboard();
      syncLobbyView();
    } catch (err) {
      console.error('leaderboard 取得失敗:', err);
    }
  })();
};

// 通算対戦成績（ローカル永続）。
const STATS_KEY = 'pool.stats';
interface MatchStats {
  wins: number;
  losses: number;
}
const loadStats = (): MatchStats => {
  const s = readJson<MatchStats>(STATS_KEY);
  if (s && typeof s.wins === 'number' && typeof s.losses === 'number') {
    return { wins: s.wins, losses: s.losses };
  }
  return { wins: 0, losses: 0 };
};
const matchStats = loadStats();
// サーバー成績モード（PartyKit で claimName 成功時 true）。true の間は名前キーの
// サーバー成績を権威とし、ローカル localStorage には書かない（端末間で共有される）。
let serverStats = false;
const recordResult = (
  won: boolean,
  opts?: { targetWins?: 1 | 3 | 5 | 9; racksPlayed?: number },
): void => {
  if (won) matchStats.wins += 1;
  else matchStats.losses += 1;
  if (serverStats) {
    // サーバー権威・onStats で最終同期。targetWins/racksPlayed は人間戦 race 別集計・累計ラック数の更新に使われる。
    lobby.reportResult(nickname, won, opts);
  } else {
    writeJson(STATS_KEY, matchStats); // ローカル（ループバック/オフライン）
  }
  lobby.setStats(matchStats); // presence の自己申告成績を更新（ループバックの順位表示用）
};
// 認証成功（ok/local）でログイン確定＝名前保存・成績反映・在席公開・順位表更新。
const applyAuthOk = (name: string, r: AuthResult): void => {
  nickname = name;
  saveNickname(name);
  if (r.status === 'ok' && r.stats) {
    serverStats = true; // サーバー権威成績（端末間共有）
    matchStats.wins = r.stats.wins;
    matchStats.losses = r.stats.losses;
    // 管理人フラグとインベントリを反映（authResult.stats に同梱されている場合のみ）。
    inventory.setAdmin(r.stats.isAdmin === true);
    if (r.stats.inventory) {
      inventory.apply({
        owned: r.stats.inventory.owned,
        // exactOptionalPropertyTypes: undefined を含めない
        equipped: Object.fromEntries(
          Object.entries(r.stats.inventory.equipped).filter(([, v]) => typeof v === 'string'),
        ) as Readonly<Partial<Record<'cue' | 'cloth' | 'badge', string>>>,
      });
    } else {
      // サーバーログイン直後にまだ inventory が同梱されていない場合は明示的に取得する。
      void lobby
        .getInventory(name)
        .then((inv) => {
          if (inv) {
            inventory.apply({
              owned: inv.owned,
              equipped: Object.fromEntries(
                Object.entries(inv.equipped).filter(([, v]) => typeof v === 'string'),
              ) as Readonly<Partial<Record<'cue' | 'cloth' | 'badge', string>>>,
            });
          }
        })
        .catch((err) => console.error('inventory 初期取得失敗:', err));
    }
    // サーバー権威のドリル進捗をローカルへ和集合マージして復元（未送信のローカル達成は失わない）。
    if (r.stats.drill) {
      drillProgress = mergeDrillProgress(drillProgress, r.stats.drill);
      saveDrillProgress(drillProgress);
    }
  } else {
    serverStats = false; // ローカル成績のまま（loopback/オフライン）
    inventory.setAdmin(false);
  }
  lobby.join(name);
  lobby.setStats(matchStats);
  refreshLeaderboard();
  syncLobbyView();
  // 設定パネル下部のアカウント情報セクションを更新（serverStats のみ表示対象）。
  controlsApi.setAccountInfo(name, serverStats);
};

/** ログアウト処理: クライアント状態を未ログイン相当へリセットして認証モーダルへ戻す。
 *  仕様:
 *   - `pool.nickname` を localStorage から削除（誤った自動ログインを防止）
 *   - `pool.clientId` は残す（次回 PIN 入力で acct.clientIds に再紐付けされる・利便維持）
 *   - サーバー成績はサーバー側に残る（同名再ログインで復元）
 *   - インベントリ表示・管理人特権・ロビー在席・対戦中状態を全てクリア
 *   - 認証モーダルを「ログイン」セグメントで開く（ニックネームと PIN を再入力させる） */
const performLogout = (): void => {
  // ロビー在席を抜ける（onRoster 経由で他クライアントから消える）。
  try {
    lobby.setWaiting(false);
    lobby.setPlaying(null);
    lobby.setSpectating(null);
  } catch (err) {
    console.error('logout lobby cleanup 失敗:', err);
  }
  // 対戦中なら teardown して通常状態へ戻す。
  if (netConnected) {
    try {
      teardownMatch();
    } catch (err) {
      console.error('logout teardownMatch 失敗:', err);
    }
  }
  // ニックネームを localStorage から削除（次回起動時の自動ログインを止める）。
  try {
    localStorage.removeItem(NICK_KEY);
  } catch (err) {
    console.error('logout nickname clear 失敗:', err);
  }
  // クライアント側の認証状態と統計表示をリセット。
  nickname = '';
  serverStats = false;
  matchStats.wins = 0;
  matchStats.losses = 0;
  // インベントリ（保有・装備・管理人フラグ）をクリア。
  inventory.setAdmin(false);
  inventory.apply({ owned: [], equipped: {} });
  // controls 側の表示更新（アカウントセクション非表示・presence 解除）。
  controlsApi.setAccountInfo('', false);
  // 認証モーダル再表示（空ニックネーム → openName('') → セグメント表示）。
  controlsApi.startAuthFlow('');
};

// ---- Screen Wake Lock ----
// 対戦/観戦中は画面スリープを抑止する（相手のショット待ち中に画面が消えるのを防ぐ）。
// 非対応ブラウザ・取得失敗時は静かに no-op（ゲーム進行に影響しない）。
const wakeLock = createWakeLockController();

// ---- race-to-N（先取数）対戦の状態管理 ----
// race は人間対戦・CPU 対戦の両方で使用。永続化なし（中断＝破棄）。
let currentRace: RaceState | null = null;
// 現 race 中に自分が達成したブレイクラン回数。targetWins と一致すれば Perfect Game。
// race 開始/teardown でリセット。ソロ（race なし）では未使用。
let raceBreakRunByMe = 0;
// CPU 対戦中フラグ。true のとき transport 不要・成績/分析を集計しない。
let cpuMatch = false;
// 現在対戦中の CPU プレイヤー（マッチ確定時に設定し、planShot のスタイル・UI 表示名を駆動）。
// null = 対戦していない / 人間対戦中。
let currentCpuPlayer: CpuPlayer | null = null;
// CPU 挑戦リクエスト時に「どの CPU と対戦するか」を保持（モーダル選択完了まで保持）。
// onChallengeCpu でロビー roster から推測したい場合に使う（現状は onMatched 経由で渡るので未使用だが、
// onChallengeCpu 経由のフローでも displayName 表示のために保持する）。
let pendingCpuPlayer: CpuPlayer | null = null;
// CPU 思考のスケジュール（手番開始から発射までの「考えている」演出時間）。
let cpuThinkTimer = 0;

// ---- バックグラウンド待機モード ----
// 「対戦相手を選ぶ」モーダルで「別の人が来るまで待機する」を押下した状態。
// amWaiting=true（lobby に waiting 登録）を維持しつつ picker は閉じ、ソロプレイを可能にする。
// 新規 waiting 対人が現れたら対戦勧誘ダイアログで通知する。
let backgroundWaiting = false;
// 同一 userId に対する対戦勧誘ダイアログの重複通知抑止 Set（メモリのみ＝リロードでクリア）。
// - 対戦成立／waiting OFF／CPU 戦開始/終了／試合終了でクリア
// - roster から該当 userId が消えた時は個別削除（再オンライン時に再通知可）
const notifiedOpponentIds = new Set<string>();
const clearMatchInvitationNotified = (): void => {
  notifiedOpponentIds.clear();
};
// race 開始待ち（モーダル選択待機）。'humanPair' = ロビーペア成立中・'cpu' = CPU 挑戦中。
let pendingRaceStart:
  | { readonly kind: 'humanPair'; readonly matchId: string; readonly role: 'host' | 'guest'; readonly opponentName: string }
  | { readonly kind: 'cpu' }
  | null = null;

// CPU が撞いたショットの間のみトップビューを強制 ON にするフラグ。
// CPU shoot 発火時に true をセット、人間 onShoot 時に false（ユーザー設定優先）、
// ショット完了時にリセット。これにより「人間のショット = ユーザー設定通り」「CPU の
// ショット = トップビュー強制」の挙動になる。
let shotByCpu = false;
// race 継続時の自動次ラックタイマー（人間戦・1.4 秒）。teardownMatch で解除する。
let raceContinueTimer = 0;

// ショット中の真上ビュー判定: 人間ショットはユーザー設定、CPU ショットは強制 ON。
const useAutoTopForShot = (): boolean => isAutoTopAfterShot() || shotByCpu;

// race スコアボード HUD を更新する（currentRace の状態を反映）。
const syncRaceScore = (): void => {
  if (!currentRace || (!netConnected && !cpuMatch)) {
    controlsApi.setRaceScore(null);
    return;
  }
  const myWins = myPlayer === 0 ? currentRace.winsP0 : currentRace.winsP1;
  const oppWins = myPlayer === 0 ? currentRace.winsP1 : currentRace.winsP0;
  controlsApi.setRaceScore({
    myWins,
    oppWins,
    targetWins: currentRace.targetWins,
    rackIndex: currentRace.rackIndex,
    oppName: currentRace.opponentName,
    vsCpu: currentRace.vsCpu,
  });
};

// 再戦の合意状態と、勝敗記録の二重計上防止。
let localWantRematch = false;
let opponentWantRematch = false;
let recordedThisGame = false;
// 個人ショット分析（撞前に begin で文脈保存・静止後に finalize で集計値を作る）。
let analyticsCtx: AnalyticsContext | null = null;
let analyticsPhaseBefore: Phase | null = null;
// ソロ（オフライン）の RACK CLEARED 後、自動で次ラック（ブレイク）へ移す予約タイマー。
let autoResetTimer: number | null = null;

// §7A: 相手選択モーダルに渡す候補リストを構築。
// - waiting 中の他者（自分以外）+ CPU プレイヤー全員
// - 表示順: CPU を先頭固定、人間は userId 昇順
const buildOpponentCandidates = (): readonly {
  readonly userId: string;
  readonly nickname: string;
  readonly isCpu: boolean;
}[] => {
  const out: { userId: string; nickname: string; isCpu: boolean }[] = [];
  // CPU を先頭に追加（lobby roster には CPU が居ない場合もあるので別ソースから組み立て）
  for (const cpu of getCpuPlayers()) {
    out.push({ userId: cpu.id, nickname: cpu.displayName, isCpu: true });
  }
  // waiting 中の他人間
  const humans = lastRoster
    .filter((u) => !isCpuUserId(u.userId) && u.userId !== lobby.userId && u.status === 'waiting')
    .slice()
    .sort((a, b) => (a.userId < b.userId ? -1 : 1));
  for (const u of humans) {
    out.push({ userId: u.userId, nickname: u.nickname, isCpu: false });
  }
  return out;
};

// §7B: 前回のラック設定を localStorage に保存/復元（次回ダイアログのデフォルト値）。
const RACE_CONFIG_KEY = 'pool.race.lastConfig';
const isRaceTargetWins = (v: number): v is 1 | 3 | 5 | 9 =>
  v === 1 || v === 3 || v === 5 || v === 9;
const isBreakRuleValue = (v: string): v is 'alternate' | 'winner' =>
  v === 'alternate' || v === 'winner';
const loadLastRaceConfig = (): RaceConfig => {
  try {
    const raw = localStorage.getItem(RACE_CONFIG_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { targetWins?: number; breakRule?: string };
      if (
        typeof parsed.targetWins === 'number' &&
        typeof parsed.breakRule === 'string' &&
        isRaceTargetWins(parsed.targetWins) &&
        isBreakRuleValue(parsed.breakRule)
      ) {
        return { targetWins: parsed.targetWins, breakRule: parsed.breakRule };
      }
    }
  } catch {
    /* localStorage 不可環境 / JSON parse 失敗は無視 */
  }
  return { targetWins: 3, breakRule: 'alternate' };
};
const saveLastRaceConfig = (race: RaceConfig): void => {
  try {
    localStorage.setItem(RACE_CONFIG_KEY, JSON.stringify(race));
  } catch {
    /* localStorage 不可環境は無視 */
  }
};

// 球数が変わる（モード変更・プラクティスの追加削除）たびに球メッシュを作り直す。
const rebuildBallViews = (): void => {
  disposeBallViews(views, ctx.scene);
  views = createBallViews(snapshot(table), ctx.scene);
};

const cue = new Cue();
ctx.scene.add(cue.mesh);
ctx.scene.add(cue.helperMesh);
ctx.scene.add(cue.shadowMesh);
ctx.scene.add(cue.placerMesh);
cue.aimMode();
cue.showHelper(true);
// 起動時の装備状態を反映（未ログイン/未取得時は何もしない＝既定色）。
// 以後の装備変更は inventory.subscribe コールバックで自動反映される。
// === 手番別キュー外観の動的適用 ===
// 対戦中（オンライン人間戦 / CPU 戦）は ruleState.current（撞き手）の所有キューを表示する。
// 自分の手番 = inventory ストアから自分の装備 effects、相手の手番 = lobby.onStats で配信された
// 相手 stats.inventory snapshot から effects を解決する。snapshot 未取得（CPU 戦の相手手番・
// マッチ直後で stats 未到達）は空 effects＝既定木製として正規化する。
//
// 設計理由: キュー mesh は self/opponent で共有（1 オブジェクト）のため、毎ターン外観を
// 切り替える必要がある。「自分の装備カーボンが相手手番でも表示される」UX 事故の修正。
const knownInventoriesByName = new Map<string, InventorySnapshot>();
let lastAppliedShaftColor: string | undefined = undefined;
let appliedShaftInit = false; // 初回適用済みフラグ（undefined の既定色と「未適用」を区別）

const resolveCueEffectsFromSnapshot = (snap?: InventorySnapshot): ItemEffects => {
  if (!snap) return {};
  const cueId = snap.equipped['cue'];
  if (typeof cueId !== 'string') return {};
  return getItemDefinition(cueId)?.effects ?? {};
};

const activeCueEffects = (): ItemEffects => {
  // ソロ/プラクティス（非対戦・非観戦）: 自分の装備をそのまま使う。
  if (!netConnected && !cpuMatch) return inventory.effectiveCueEffects();
  // 観戦中: 撞き手の名前を確実に取得する API が無いため Phase 1 では既定（木製）で許容。
  // 必要になったら lobby spectate からプレイヤー名→snapshot 解決を追加する。
  if (spectating) return {};
  // 対戦中: ruleState.current が自分なら自分の装備、相手なら相手の snapshot。
  if (ruleState.current === myPlayer) return inventory.effectiveCueEffects();
  if (opponentNickname.length > 0) {
    return resolveCueEffectsFromSnapshot(knownInventoriesByName.get(opponentNickname));
  }
  return {}; // CPU 戦の相手手番（CPU は inventory を持たない）
};

const applyActiveCueAppearance = (): void => {
  const effects = activeCueEffects();
  // 既に同色を適用済みなら毎フレーム/毎 syncRuleHud で呼ばれても何もしない（material 書込節約）。
  if (appliedShaftInit && effects.shaftColor === lastAppliedShaftColor) return;
  applyCueShaftAppearance(cue, effects.shaftColor);
  // shaftColor 指定あり = ダーク系シャフト → 寒色（クールホワイト）で木製感を消す。
  // 既定（メイプル木）は暖色のまま温かい光を残す。
  cueGlimmer.setGlowColor(effects.shaftColor ? '#aac4ff' : null);
  lastAppliedShaftColor = effects.shaftColor;
  appliedShaftInit = true;
};

// オートエイムの自動補正時にキューを一瞬きらめかせる演出（自前・GPL 非依存）。
// ★ applyActiveCueAppearance() より前に宣言すること（TDZ 回避）。同関数 body が
//   cueGlimmer.setGlowColor を呼ぶため、初回適用呼び出しの前に cueGlimmer が存在する必要がある。
const cueGlimmer = createCueGlimmer(cue.mesh);

// 初回適用（自分のキュー or 既定木製）。cueGlimmer 宣言後に呼ぶこと（TDZ 回避）。
applyActiveCueAppearance();

// ショット力インジケーター（自前・GPL非依存）。照準ガイド方向に予測挙動を色帯で重ねる。
const shotGuide = createShotGuide(ctx.scene, ballRadius);
// 予測軌跡（手球の実シミュレート・スピン/クッション/カーブ込み）。
const REFLECT_MAX_POINTS = 400; // 点列の上限（=描画長の上限 ≈ 400×R ≈ 13m）
const reflectGuide = createReflectGuide(ctx.scene, ballRadius, REFLECT_MAX_POINTS);
// カット角ガイド: 的玉とポケット狙点を対角に、手玉→的玉ライン軸 u と直交 n を辺方向とする
// 長方形（直角四辺形）を表示する。設定ON時のみ静止中に表示。45°で正方形になる。
const cutAngleFrame = createCutAngleFrame(ctx.scene);
// ポケット候補グロー: 自動照準が「狙える」と判定したポケット候補を緑半透明で可視化（タップで切替）。
const pocketGlows = createPocketGlows(ctx.scene);
// ドラッグ位置ガイド（自前・GPL非依存・SVGオーバーレイ）。
// 構え中、canvas 上の「ショット方向ドラッグ範囲」と「仰角/キュー手元ドラッグ範囲」を
// screen-space 半透明矢印 + 掴み幅 capsule で可視化する。grab 半径は controls.ts と同期。
const dragGuide = createDragGuide(canvas, AIM_GRAB_PX_EXPORT);
// パワー未設定（キュー未引き）時の既定プレビュー強さ（≈4クッション相当）。
const DEFAULT_PREVIEW_POWER = cue.maxPower * 0.5;
let reflectPath: PathPoint[] = [];
let lastReflectSig = '';
const GUIDE_MAX_LEN = ballRadius * 60; // 的玉が無い時の帯の上限長（helper 長に合わせる）
const GUIDE_NEUTRAL = 0xe6e6e6; // 的玉なし（色不要・到達距離のみ）
const GUIDE_RED = 0xff3b30; // 的玉に届かない
const GUIDE_YELLOW = 0xffd400; // 的玉に届く（衝突時は転がり=押し）
const GUIDE_GREEN = 0x33d17a; // STOP（スタン）になる強さ

// 起動時は手球の後方からショット方向(+x=ラック)を正面に見る（azimuth = aim角 + π）。
// radius は手球を手前下、ラックを画面奥に置く構図（テーブル長辺が画面内に収まる）。
// アスペクト比対応: portrait は基準 1.7、landscape では sqrt(aspect) で近寄せ（縦が短いと
// 同じ radius では table が小さく見えるため）。clamp で過剰な接近を防ぐ。
// 狙撃カム ON 時は ⊕5回押下相当のズーム倍率（SHOT_FREEZE_CAM_ZOOM_FACTOR ≈ 0.59）を乗じ、
// 構え時のデフォルト半径を縮める（的玉を大きく見せて狙いやすくする）。
const BASE_PORTRAIT_RADIUS = 1.7;
const computeAspectRadius = (): number => {
  const aspect = window.innerWidth / window.innerHeight;
  let r: number;
  if (aspect <= 1) {
    r = BASE_PORTRAIT_RADIUS;
  } else {
    // landscape ほど近寄せ（1.5～2.5 で 0.82～0.63 倍 = 1.39～1.07 m）
    const scale = Math.max(0.6, Math.min(1.0, 1 / Math.sqrt(aspect)));
    r = BASE_PORTRAIT_RADIUS * scale;
  }
  if (isShotFreezeCam()) r *= SHOT_FREEZE_CAM_ZOOM_FACTOR;
  return r;
};
const rig = createCameraRig(ctx.camera, {
  radius: computeAspectRadius(),
  elevation: 0.45,
  azimuth: cue.aim.angle + Math.PI,
});
// 画面回転/リサイズで radius を再計算（手動 orbit/zoom 前のみ自動追従）。
window.addEventListener('resize', () => {
  if (rig.isUserOverridden()) return;
  const r = computeAspectRadius();
  rig.setRadius(r);
  rig.setInitialRadius(r);
});
rig.setAimTarget(table.cueball.pos.x, table.cueball.pos.y, table.cueball.pos.z);
rig.setFraming(true);
// トップビューのフィット計算用にテーブル外周半幅を渡す（常に画面フィット）。
const tl = getTableLayout();
rig.setTopFitExtents(railOuterX(tl.halfX), railOuterY(tl.halfY));

// F-31 P2 Director state（先行宣言）— buildBroadcastSceneContext / 起動時の初期適用が
// `lastApproachedPocket` を読み取るため、関数定義より前に宣言する必要がある（TDZ 回避）。
// 詳細な Director 配線は本ファイル下方の dispatchDirector / animate ループで行う。
let directorState: DirectorState = initDirectorState(0);
let lastUserOverrideObserved = false;
let lastRulePhaseObserved: 'break' | 'normal' | 'win' = 'normal';
// F-31 P3 §14.4: shot-fired 直前の事前選定で使う狙うポケット位置。
// per-frame の pocket-approach 検知は P3 で廃止。本変数は SceneContext.aimedPocket への
// フォールバック（手玉延長線推定）でのみ参照される。
let lastApproachedPocket: { x: number; y: number } | null = null;

// F-31 P3 §14.2.4 idle-too-long 検知 — Broadcast Master が継続して 30 秒以上経過したら
// 1 回だけ `idle-too-long` を dispatch（WIDE へ視覚的変化）。current が変わると再武装される。
// 旧 P2b は POV ベース・JIB 行きだったが、P3 で BM ベース・WIDE 行きへ変更。
const IDLE_TOO_LONG_MS = 30_000;
let idleTooLongDispatchedFor: string | null = null;

// F-31 P2b §12.3 Framing Safety — Director モード時、ショット中に手玉+的玉+狙うポケットが
// 連続して画面外に出続けたら 1 ショット 1 回 `framing-lost` を dispatch（POV へフォールバック）。
// FRAMING_MARGIN_M は球数個分の余裕（境界判定 fail で球の一部見切れを救済）。
const FRAMING_LOST_TIMEOUT_MS = 300;
const FRAMING_MARGIN_M = 4 * ballRadius;
let framingLostStartWallMs: number | null = null;
let framingLostDispatchedThisShot = false;

// F-31 P2b §12.2 foul-detected — 直近の foul-detected dispatch を 1 ショット 1 回に制限する
// （applyRuleResult が複数フレームに跨る場合の重複抑止）。
let foulDispatchedThisShot = false;

// F-31 P3.4 §14.14 first-contact — 手玉と他球（または的玉同士）の最初の ball-ball collision
// を 1 ショット 1 回検出して dispatch する。cushion（レール接触）は対象外＝kick shot で先に
// レールに当たる場合は無視され、的玉に当たった瞬間に POCK 切替が走る。
let firstContactDispatchedThisShot = false;

// F-31 P2b §12.6 9 番勝利特別シーケンサ — ball-pot isWinBall=true 検知時刻を起点に、
// T+0.3 で setSlowMotion(0.5)・T+0.8 で win-sequence-tophead（→ Hot Head）・T+3.0 で
// setSlowMotion(1.0) 復帰の 4 段スケジューラ。リセット時／新ショット発射時にクリア。
const WIN_SEQ_SLOW_AT_MS = 300;
const WIN_SEQ_TOPHEAD_AT_MS = 800;
const WIN_SEQ_END_AT_MS = 3000;
const WIN_SEQ_SLOWMO_SCALE = 0.5;
let winSequenceStartWallMs: number | null = null;
let winSeqSlowApplied = false;
let winSeqTopHeadDispatched = false;

// F-31 Broadcast カメラモード — 現在の盤面文脈を集めて SceneContext を組み立てる純粋関数。
// プリセット apply() が必要とする最小情報のみ。GPL 非依存（cueBall/lowestOnTableBallId は world 経由）。
const buildBroadcastSceneContext = (): SceneContext => {
  const cb = table.cueball.pos;
  const targetId = lowestOnTableBallId(table);
  const targetPos = targetId !== null ? ballPositionById(table, targetId) : null;
  // ルール時 phase を Broadcast の 3 値（'break' | 'normal' | 'win'）へ写像。
  // breakPlacement → 'break'、それ以外（aim/result/end 等）は 'normal'。
  const phase: 'break' | 'normal' | 'win' =
    rulesActive && ruleState.phase === 'breakPlacement' ? 'break' : 'normal';
  // aimedPocket: pocket-approach 検出済みならその位置を優先（Director の Pocket Cam 切替直後）。
  // それ以外は手玉→次球の延長線で最近接ポケット（auto-aim フォールバック流用）。
  // 構え中も「狙うポケット」が決まるため #5 Pocket Cam が常に適切な構図を取れる。
  let aimedPocket: { x: number; y: number } | null = lastApproachedPocket;
  if (!aimedPocket && targetPos) {
    const pockets = getPocketAims().map((p) => ({ x: p.x, y: p.y }));
    const resolved = fallbackTargetPocket(
      { x: cb.x, y: cb.y },
      { x: targetPos.x, y: targetPos.y },
      pockets,
    );
    aimedPocket = resolved ? resolved.pos : null;
  }
  return {
    cueBall: { x: cb.x, y: cb.y },
    targetBall: targetPos ? { x: targetPos.x, y: targetPos.y } : null,
    aimedPocket,
    shotAimAngle: cue.aim.angle,
    tableHalfX: tl.halfX,
    tableHalfY: tl.halfY,
    phase,
    // ラック側は P1 既定 'foot'（プリセット #3 Low-Rail で方向決定にのみ使用）。
    // 厳密にはルール時の break 側プレイヤーから決定すべきだが P2b 以降で詳細化する。
    racker: 'foot',
  };
};

// F-31 Broadcast HUD ピルバー（上中央・スマホ向け手動切替 UI）。Broadcast ON 時のみ表示。
// 4 ボタン（POV/TOP/LOW/WIDE）を BROADCAST_PRESETS から動的生成（ハードコード禁止ルール準拠）。
// 現在適用中のプリセットを `is-active` でハイライト（is-active クラスを正本＝SSoT として状態保持）。
// タップで preset.apply() を呼ぶ。P2 で Director 状態機械が同じ applyBroadcastPreset を介する。
const broadcastBar = document.createElement('div');
broadcastBar.className = 'pool-broadcast-bar';
broadcastBar.setAttribute('role', 'toolbar');
broadcastBar.setAttribute('aria-label', 'Broadcast カメラプリセット');
broadcastBar.hidden = true;
const broadcastBarButtons = new Map<BroadcastPresetId, HTMLButtonElement>();

// P3.5（撤回）/ P3.6: 適応的透過は Director 自動切替のたびにパルスし「ちらつき」と
// 認識されたため撤回。代わりに **設定 ON/OFF + 観戦時のみ自動 ON** で可視性を制御する
// （syncBroadcastBarVisibility 参照）。バーが表示されているときは常に不透明。

const applyBroadcastPreset = (preset: BroadcastPreset): void => {
  preset.apply(rig, buildBroadcastSceneContext());
  for (const [id, btn] of broadcastBarButtons) {
    btn.classList.toggle('is-active', id === preset.id);
  }
};
for (const preset of BROADCAST_PRESETS) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'pool-broadcast-btn';
  btn.setAttribute('aria-label', `${preset.displayName}（キー ${preset.key}）`);
  btn.title = `${preset.displayName} — ${preset.description}`;
  const keySpan = document.createElement('span');
  keySpan.className = 'pool-broadcast-btn-key';
  keySpan.textContent = preset.key;
  btn.appendChild(keySpan);
  const labelSpan = document.createElement('span');
  labelSpan.className = 'pool-broadcast-btn-label';
  labelSpan.textContent = preset.shortLabel;
  btn.appendChild(labelSpan);
  btn.addEventListener('click', (ev) => {
    ev.preventDefault();
    applyBroadcastPreset(preset);
  });
  broadcastBar.appendChild(btn);
  broadcastBarButtons.set(preset.id, btn);
}
document.body.appendChild(broadcastBar);

// P3.6 §14.16: バー表示判定の更新。
// 可視条件: Broadcast ON (mode !== 'off') AND (設定で表示 ON か 観戦中)。
// Broadcast OFF 時は無条件に非表示。観戦中（spectating=true）は設定に関わらず強制表示。
const syncBroadcastBarVisibility = (): void => {
  const on = isBroadcastOn();
  const shouldShow = on && (isBroadcastBarVisible() || spectating);
  broadcastBar.hidden = !shouldShow;
  // body クラスは「Broadcast ON で何らかの UI が見える可能性」のシグナル＝
  // パレットの位置調整等で使うが、本実装でも従来通り on の有無で切替。
  document.body.classList.toggle('pool-broadcast-on', on);
};

// キーボード 1〜6 で Broadcast プリセットを切替（PC/Bluetooth キーボード接続時用）。
// Broadcast OFF 時は無視（既存の数字キーショートカット領域を奪わない）。
// テキスト入力中（input/textarea フォーカス）も無視。
window.addEventListener('keydown', (ev) => {
  if (!isBroadcastOn()) return;
  const tgt = ev.target;
  if (
    tgt instanceof HTMLInputElement ||
    tgt instanceof HTMLTextAreaElement ||
    (tgt instanceof HTMLElement && tgt.isContentEditable)
  ) {
    return;
  }
  if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
  const preset = findBroadcastPresetByKey(ev.key);
  if (!preset) return;
  ev.preventDefault();
  applyBroadcastPreset(preset);
});

// Broadcast モード切替時は OFF→ON で player-pov 既定プリセットを適用、ON→OFF で
// rig 状態を「通常追従」へ戻す（top lock 解除・overview 解除等）＋ HUD バーの可視性を同期。
subscribeBroadcastModeChange(() => {
  syncBroadcastBarVisibility();
  if (isBroadcastOn()) {
    const player = findBroadcastPresetByKey('1');
    if (player) applyBroadcastPreset(player);
  } else {
    // 既定の自動演出が再び有効になる前に、Broadcast プリセットが残したロック状態を解除。
    rig.setTopViewLocked(false);
    rig.setAutoTopView(false);
    rig.setOverview(false);
    rig.clearOrbitTarget();
    for (const btn of broadcastBarButtons.values()) {
      btn.classList.remove('is-active');
    }
  }
});

// P3.6 §14.16: `pool.broadcastBarVisible` 設定変化でもバー可視性を再 sync する。
subscribeBroadcastBarVisibilityChange(() => {
  syncBroadcastBarVisibility();
});

// 起動時の初期適用: Broadcast 既定値が 'director' の新規ユーザーには player-pov を適用する。
// 既存ユーザーが 'off' を保存している場合は何もしない。
syncBroadcastBarVisibility();
if (isBroadcastOn()) {
  const initial = findBroadcastPresetByKey('1');
  if (initial) applyBroadcastPreset(initial);
}

// F-31 P2 Director — 状態機械を main.ts 側で管理。reducer は純関数（broadcast-director.ts）。
// `getBroadcastMode() === 'director'` のときのみ action を dispatch する。
// Manual モードでは HUD タップ／キーボードから直接 applyBroadcastPreset を呼ぶため
// Director state は更新しない（衝突は canSwitchTo の同一プリセット判定で吸収）。
// 注: state 変数（directorState / lastUserOverrideObserved / lastRulePhaseObserved /
// pocketApproachDispatchedThisShot / lastApproachedPocket）と定数（POCKET_APPROACH_*）は
// buildBroadcastSceneContext の TDZ 回避のため**本ブロックより上**で先行宣言済み。

const dispatchDirector = (action: DirectorAction): void => {
  if (getBroadcastMode() !== 'director') return;
  const prev = directorState;
  const next = reduceDirector(prev, action);
  if (prev === next) return;
  directorState = next;
  // applyDirectorEffects 内で `prev.current === next.current` を確認するため、
  // tick action（current 不変）では rig には触らない（安全）。
  applyDirectorEffects(prev, next, rig, buildBroadcastSceneContext());
  // HUD バーの is-active ハイライトも追従させる。
  if (prev.current !== next.current && next.current !== 'transition') {
    for (const [id, btn] of broadcastBarButtons) {
      btn.classList.toggle('is-active', id === next.current);
    }
  }
};

/** 現在のルールフェーズを Director の 'break' | 'normal' | 'win' へ写像する。
 *  ruleState.phase === 'breakPlacement' を break、game-over 系を win、それ以外を normal。 */
const resolveDirectorPhase = (): 'break' | 'normal' | 'win' => {
  if (!rulesActive) return 'normal';
  if (ruleState.phase === 'breakPlacement') return 'break';
  if (ruleState.phase === 'gameOver') return 'win';
  return 'normal';
};

// 「次に狙うべき的玉」が他球で完全に遮蔽されているか判定する。
// 設定「対象球が隠れたら自動トップビュー」用。中心ライン1本だけでは球の半径が
// 考慮されず誤判定になるため、**対象球に当たり得る角度の窓**（中心-中心距離から
// 求まる接線角 ±asin(2R/d)）を細かくサンプリングし、いずれかの角度で
// `aimedTargetId === target` なら「直接当てられる経路あり＝隠れていない」とする。
// 全サンプルで遮蔽されている時のみ true（＝完全に隠れている）を返す。
// これにより、少し見えていてカットショットで当てられる場合は切替なし、という挙動になる。
const OBSCURED_SAMPLES = 11; // 窓内サンプル数（奇数で中心を含む）
const isTargetObscured = (): boolean => {
  if (!table.cueball.onTable()) return false;
  const targetId = lowestOnTableBallId(table);
  if (targetId === null) return false;
  const targetPos = ballPositionById(table, targetId);
  if (!targetPos) return false;
  const dx = targetPos.x - table.cueball.pos.x;
  const dy = targetPos.y - table.cueball.pos.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-4) return false;
  const baseAngle = Math.atan2(dy, dx);
  // 対象球に当たり得る角度の半幅（接線角・asin の引数は安全クランプ）。
  // 端ぎりぎりは数値的に不安定なので 0.95 を掛けて少し内側を見る。
  const halfWindow = Math.asin(Math.min(0.999, (2 * ballRadius) / dist)) * 0.95;
  // OBSCURED_SAMPLES は 2 以上の固定値。中央を含む左右対称サンプル。
  for (let i = 0; i < OBSCURED_SAMPLES; i++) {
    const t = i / (OBSCURED_SAMPLES - 1);
    const a = baseAngle + (t - 0.5) * 2 * halfWindow;
    if (aimedTargetId(table, a) === targetId) return false; // 直接当てられる経路あり
  }
  return true; // 全角度で遮蔽 → 完全に隠れている
};

// 最若番号の的玉へ照準を向ける（手球が盤上の時のみ。的玉が無ければ何もしない）。
const aimAtLowestBall = (): void => {
  if (!table.cueball.onTable()) return;
  const targetId = lowestOnTableBallId(table);
  if (targetId === null) return;
  const t = ballPositionById(table, targetId);
  if (!t) return;
  const cb0 = table.cueball.pos;
  cue.aim.angle = Math.atan2(t.y - cb0.y, t.x - cb0.x);
  // 残球表示の強調を最若球に合わせる（間に障害球があっても意図した的玉を強調）。
  controlsApi?.setAimTarget(targetId);
};

// 盤面を再ラック→最若球へ照準→カメラ初期化→ブレーク配置モード。
// リセット・モード変更・全的球ポケットイン（再ラック）で共用する。
//   useStoredLayout=true で呼ぶと、プラクティス時のみ「ユーザー保存配置」を優先適用する
//   （onReset 経由のリセット時のみ true で呼ぶ。モード変更/再ラックは false でデフォルトに戻す）。
const resetRackAndAim = (mode: PlayMode, useStoredLayout = false): void => {
  // 再ラック/モード変更は（クリア後の表示中も含め）ドリル状態を必ず後始末し通常プレイへ戻す。
  // currentDrill が既に null（クリア後）でもロック/ゲージが残るため、常に解除する。
  currentDrill = null;
  drillPanel?.endSession();
  controlsApi.setDrillLock(false); // 配置ロック解除（通常プレイへ戻す）
  // 予約済みの自動リセットがあれば取り消す（手動リセット/モード変更で重複発火しないように）。
  if (autoResetTimer !== null) {
    clearTimeout(autoResetTimer);
    autoResetTimer = null;
  }
  // ショット飛行中のリセット/相手退出（teardownMatch 経由）でも、次フレームの静止処理（settle）が
  // 「新ラックに対して旧ショットの終了処理」として誤発火しないよう、ショット進行状態を確実に破棄する。
  // （以前は shotActive が残存し、再ラック直後に recordShot(0以下) が走って連続ブレイクラン記録が消えていた）
  shotActive = false;
  shotByCpu = false;
  preShot = null;
  shotOutcomes = [];
  pendingDoubleHit = false;
  analyticsCtx = null;
  breakHeadStringIds.clear();
  // 狙撃カム凍結が残らないよう確実に解除（rig.reset でもクリアされるが二重保険）。
  if (shotFreezeCamActive) {
    rig.releaseFreeze(0);
    shotFreezeCamActive = false;
    shotFreezeTargetBallId = null;
    shotFreezePocketPos = null;
    shotFreezeReleased = true;
  }
  resetWorld(table, mode);
  // プラクティス × リセット要求時のみ、保存配置があればそれを適用してデフォルト盤面を上書きする。
  //   注意: `restoreBalls` は球数一致を要求するため、プラクティスのように球数が可変なケースでは
  //   そのまま渡しても弾かれる。先に「保存配置の球集合」に盤面を合わせ（不要な球を removeBall ／
  //   不足球を addBall）、最後に正確な位置を `placeBallAt` で配置する。
  if (useStoredLayout && mode === 'practice') {
    const stored = controlsApi.loadPracticeLayout();
    if (stored) {
      const storedIds = new Set(stored.map((b) => b.id));
      // 保存配置に含まれない盤上の球（手玉以外）を削除。
      const currentIds = table.balls.map((b) => b.id);
      for (const id of currentIds) {
        if (id !== 0 && !storedIds.has(id)) removeBall(table, id);
      }
      // 保存配置にあって現在盤面にない球（手玉以外）を追加。
      const existingIds = new Set(table.balls.map((b) => b.id));
      for (const sb of stored) {
        if (sb.id !== 0 && !existingIds.has(sb.id)) addBall(table, sb.id, sb.x, sb.y);
      }
      // 全球（手玉含む）を保存位置へ配置（clampToTable + 重なり解消は placeBallAt が担う）。
      for (const sb of stored) placeBallAt(table, sb.id, sb.x, sb.y);
    }
  }
  rebuildBallViews();
  aimAtLowestBall();
  rig.reset(cue.aim.angle + Math.PI);
  controlsApi.enterPlacement();
  // 新しいラック開始: 進行中ランをリセット（連続記録は保持）。
  // 対戦中で相手ブレイクのラックは「自分のブレイクラン」対象外として開始（mineBreak=false）。
  // 観戦中は自分のショットがないので一律 mineBreak=false。
  const mineBreak = !spectating && (!currentRace || currentRace.breaker === myPlayer);
  breakRun.startRack(mineBreak);
  // ルールモードなら状態を初期化（ブレイカーは引き継ぐ）。
  if (rulesActive) {
    ruleState = initRuleState({ breaker: ruleState.breaker });
  }
  lastRuleResult = null;
  preShot = null;
  shotOutcomes = [];
  syncRuleHud();
  // 新しい盤面（再ラック直後の静止状態）を保存し、次回起動でここから復元できるようにする。
  persist();
};

// モードを適用して盤面・球メッシュ・カメラを初期化し、ブレーク配置モードに入る。
const applyPlayMode = (mode: PlayMode): void => {
  // モードを明示的に確定したらドリル復帰モードは破棄（ユーザーがこのモードを選んだ）。
  drillReturnMode = null;
  playMode = mode;
  rulesActive = mode === RULES_MODE;
  resetRackAndAim(mode);
};

// ドリルを終了し、入る前のプレイモードへ戻す（✕終了 / クリア後の「終了」共通）。
const exitDrill = (): void => {
  const ret = drillReturnMode ?? 'practice';
  drillReturnMode = null;
  applyPlayMode(ret); // 元モードへ戻して再ラック（resetRackAndAim 内でドリル後始末も走る）
};

const controlsApi = createControls({
  canvas,
  camera: ctx.camera,
  rig,
  cue,
  table,
  isResting: () => isResting(table),
  onReset: () => {
    // ドリル中/直後（「✕ 終了」）はドリルを終了し、入る前のプレイモードへ戻す。
    if (currentDrill || drillReturnMode !== null) {
      exitDrill();
      return;
    }
    // 通常リセット: 再ラックして最若球へ照準（カメラも初期構図へ）。
    // プラクティス時は保存配置があればそれに戻る（無ければデフォルト盤面）。
    resetRackAndAim(playMode, true);
  },
  onResetPracticeLayoutToDefault: () => {
    // 「↺ デフォルト」ボタン: 保存配置の破棄は controls 側で済んでいる。
    // ここではプラクティスのデフォルト盤面（useStoredLayout=false）を強制適用する。
    resetRackAndAim(playMode, false);
  },
  // 装備中キューのブレイク最大スピード（カーボンキュー等の効果）。装備なし or 未指定なら undefined。
  getEquippedCueBreakMaxSpeed: () => inventory.effectiveCueEffects().breakShotMaxSpeed,
  // 装備中 skill の「対戦中自動照準」許容カット角 [度]（未装備/未解禁なら undefined）。
  // 装備時のみ controls が対戦中（CPU戦・オンライン対戦）の自動照準を発動する。
  getEquippedSkillAutoAimMaxCutDeg: () => inventory.effectiveSkillEffects().autoAimMatchMaxCutDeg,
  // ドリル中に許可する自動照準の許容カット角 [度]（保有スキルの最大・owned ベース・F-30 案A）。
  // Efren 報酬スキルも owned に含まれるため自動的に対象。0（未保有）は undefined にして controls 側で無効化。
  getDrillAutoAimMaxCutDeg: () => {
    const deg = inventory.maxOwnedAutoAimCutDeg();
    return deg > 0 ? deg : undefined;
  },
  // 次のショットがブレイクかの権威判定（ルール状態由来）。ルール有効時は phase==='breakPlacement'、
  // ルール無効（プラクティス等）は undefined を返し、controls 側でローカル !broken にフォールバックさせる。
  // ★ドリルは構造上ブレイクが存在しない（1球目を broken=false の誤フォールバックでブレイク扱いし、
  //   ブレイクパワー強化が誤発動する不具合があった）。ドリル中は権威的に false を返して誤発動を断つ。
  // ★ローカル broken は相手（CPU/オンライン）のブレイクでは同期されないため、ルール有効時はこの
  //   権威信号を使って「相手ブレイク後の自分の通常ショットがブレイク扱いになる」誤発動を防ぐ。
  isBreakShot: (): boolean | undefined =>
    currentDrill ? false : rulesActive ? ruleState.phase === 'breakPlacement' : undefined,
  // 設定パネル下部の「ログアウト」が押されたとき。クライアント状態をリセットして再ログイン画面に戻る。
  onLogout: () => performLogout(),
  getPlayMode: () => playMode,
  onPlayModeChange: applyPlayMode,
  onBallsChanged: rebuildBallViews,
  onOpenDrill: () => drillPanel?.open(),
  onUndo: () => {
    // アンドゥ: 盤面は controls が復元済み。再生状態を解除し、エイムに戻す。
    shotActive = false;
    nearRestElapsed = 0;
    shotElapsed = 0;
  },
  onDrillRestart: () => {
    // ドリル中の「↻ やり直し」: 現在のドリルを配置・ショット数・残球ごと最初からやり直す。
    if (currentDrill) startDrill(currentDrill.layout);
  },
  onBeforeStrike: () => {
    // 撞く直前（全球静止）のスナップショットを保持（オンラインで相手に送り再生させる）。
    if (netConnected && !spectating) preShotSnapshot = serializeBalls(table);
  },
  onAutoAimGlimmer: () => cueGlimmer.trigger(),
  onShoot: () => {
    // 人間のショット: CPU フラグを解除（このショットはユーザー設定に従う）。
    shotByCpu = false;
    // 打撃の瞬間（手球に速度付与済み）に発音＝発火検出の1フレーム遅延を回避。
    if (isSoundEnabled()) sound.cue(cueBallSpeed(table));
    // 「対象球が隠れたら自動トップ」由来のロックは打った瞬間に解除する
    // （ショット中の真上ビューは別途 isAutoTopAfterShot()→setAutoTopView で制御）。
    rig.setTopViewLocked(false);
    // フォローカメラの的球を確定（照準先の球。なければ最若球へフォールバック）。
    followTargetId = aimedTargetId(table, cue.aim.angle) ?? lowestOnTableBallId(table);
    // 球速HUD: このショットがブレイクかを確定。ルール有効時は権威信号（撞く前の phase）で判定し、
    // ルール無効（プラクティス等）はローカル !broken にフォールバック。
    // ★onShoot はローカルの人間ショットでのみ発火するが、!broken 単独だと「相手ブレイク後に手番が
    //   回ってきた自分の最初の通常ショット」が broken=false のままブレイク誤判定される。phase は
    //   撞く前（ルール reducer 適用前）の値なので、ここで breakPlacement 判定が正しく取れる。
    shotIsBreak = rulesActive
      ? ruleState.phase === 'breakPlacement'
      : !controlsApi.getPersistState().broken;
    // ルール観測の基準を打前に確定（最小球・盤上集合・二度撞き幾何）。
    if (rulesActive) {
      preShot = beginShotObservation(table);
      // 分析: 撞く直前の手球位置・撞前フェーズ（ブレイク判定用）を保存。
      analyticsCtx = beginAnalytics(table);
      analyticsPhaseBefore = ruleState.phase;
      pendingDoubleHit = ruleParams.doubleHit.enabled
        ? detectDoubleHitGeometry(
            table,
            cue.aim.angle,
            cue.aim.elevation,
            ruleParams.doubleHit,
          )
        : false;
      shotOutcomes = [];
      breakHeadStringIds.clear(); // 新ショット: キッチン越え記録をリセット
      // 撞いた瞬間に直前ショットのファウル表示を消す（次の判定が出るまで手番のみ表示）。
      lastRuleResult = null;
      syncRuleHud();
      // オンライン: 撞く直前スナップショット＋撞き方を送信（相手がローカル再生）。
      if (netConnected && !spectating && preShotSnapshot) {
        transport?.send({
          t: 'shotStart',
          shot: currentShotPayload(),
          balls: preShotSnapshot,
        });
        preShotSnapshot = null;
      }
    } else if (currentDrill) {
      // ドリル（プラクティス基盤・ルール無効）でも観測を取り、静止後に judgeDrillShot で合否判定する。
      preShot = beginShotObservation(table);
      shotOutcomes = [];
      pendingDoubleHit = false;
      breakHeadStringIds.clear();
    }
  },
  onRackChange: () => {
    // ラック調整の保存: 現在モードで再ラックして新しい配置を反映。
    resetRackAndAim(playMode);
  },
  onHumidityChange: (h: number) => {
    // 対戦中はテーブルコンディション変更を相手へ通知。
    if (netConnected && !spectating) {
      transport?.send({ t: 'condition', humidity: h });
    }
  },
  onDeclarePushOut: () => {
    if (!rulesActive) return;
    try {
      ruleState = dispatch(ruleState, { type: 'declarePushOut' }, ruleParams).state;
    } catch (err) {
      console.error('プッシュアウト宣言に失敗:', err);
    }
    if (netConnected) {
      transport?.send({ t: 'state', state: ruleState, balls: serializeBalls(table) });
    }
    syncRuleHud();
  },
  onPushOutResponse: (takeShot: boolean) => {
    if (!rulesActive) return;
    try {
      ruleState = dispatch(
        ruleState,
        { type: 'pushOutResponse', takeShot },
        ruleParams,
      ).state;
    } catch (err) {
      console.error('プッシュアウト応答に失敗:', err);
    }
    if (table.cueball.onTable()) {
      aimAtLowestBall();
      rig.alignToAim(cue.aim.angle);
    }
    // オンライン: 状態変化を相手へ同期。
    if (netConnected) {
      transport?.send({ t: 'state', state: ruleState, balls: serializeBalls(table) });
    }
    syncRuleHud();
  },
  onLobbyWaitingToggle: () => {
    if (netConnected) return; // 対戦/観戦中は相手募集の切替を受け付けない
    // バックグラウンド待機モード中: ロビーパネル内の「待機キャンセル」ボタン押下時。
    // picker を開かず、待機を即解除する（ボタンが「対戦相手をさがす」に戻るだけ）。
    // picker を開くと「待機中… 解除する」表示でユーザーがキャンセル状態を見失うため。
    if (backgroundWaiting) {
      amWaiting = false;
      backgroundWaiting = false;
      lobby.setWaiting(false);
      controlsApi.setBackgroundWaitingMode(false);
      controlsApi.closeMatchInvitation();
      clearMatchInvitationNotified();
      void wakeLock.release();
      syncLobbyView();
      return;
    }
    amWaiting = !amWaiting;
    lobby.setWaiting(amWaiting);
    // マッチング待機中もスリープ抑止＝マッチ成立通知に即時気付けるようにする。
    // キャンセル時は解放（ソロ復帰中のバッテリー温存）。マッチ成立で startMatch/startCpuMatch
    // へ遷移した場合は acquire が冪等なのでギャップ無く保持される。
    if (amWaiting) {
      void wakeLock.acquire();
      // §7A: 相手選択モーダルを開く（候補=CPU 全員 + waiting 中の他人間）。
      controlsApi.showOpponentPicker({ candidates: buildOpponentCandidates() });
    } else {
      void wakeLock.release();
      controlsApi.closeOpponentPicker();
      // waiting OFF で通知済み Set もクリア（次回 ON 時に再通知できるよう）。
      clearMatchInvitationNotified();
    }
    syncLobbyView();
  },
  onLobbyOpen: () => refreshLeaderboard(), // 開いたら最新の順位表を取得
  onSpectate: (matchId: string) => startSpectate(matchId),
  onProfileOpen: (name: string, lastSeen: number, online: boolean) => {
    // ロビー行タップ → サーバーから個人プロファイル取得 → controls へ反映。
    // 読み込み中は openProfileLoading でパネルを開いて視覚的フィードバック。
    // 2026-06-17: 旧 Last online 列の機能をプライバシー保護のため一覧から個人画面へ移設。
    //   ロビー行が持つ lastSeen/online をそのままプロファイルへ橋渡しする。
    controlsApi.openProfileLoading(name, lastSeen, online);
    void (async (): Promise<void> => {
      try {
        const data = await lobby.getAnalytics(name);
        controlsApi.openProfile(name, data, lastSeen, online);
      } catch (err) {
        console.error('プロファイル取得失敗:', err);
        controlsApi.openProfile(name, null, lastSeen, online);
      }
    })();
  },
  // 起動/名前確定: 自動ログイン判定。ok/local は確定、それ以外は controls が次モーダルへ。
  onAuthTry: async (name: string): Promise<AuthResult> => {
    const r = await lobby.authTry(name);
    if (r.status === 'ok' || r.status === 'local') applyAuthOk(name, r);
    return r;
  },
  // 新規登録（PIN設定）。
  onRegister: async (name: string, pin: string): Promise<AuthResult> => {
    const r = await lobby.register(name, pin);
    if (r.status === 'ok') applyAuthOk(name, r);
    return r;
  },
  // PIN で端末紐づけ。
  onAuthPin: async (name: string, pin: string): Promise<AuthResult> => {
    const r = await lobby.authPin(name, pin);
    if (r.status === 'ok') applyAuthOk(name, r);
    return r;
  },
  // 名前変更（成績移送・本人のみ）。
  onRename: async (to: string): Promise<AuthResult> => {
    const r = await lobby.renameAccount(nickname, to);
    if (r.status === 'ok' || r.status === 'local') {
      nickname = to;
      saveNickname(to);
      if (r.stats) {
        matchStats.wins = r.stats.wins;
        matchStats.losses = r.stats.losses;
      }
      lobby.setNickname(to);
      lobby.setStats(matchStats);
      refreshLeaderboard();
      syncLobbyView();
    }
    return r;
  },
  // PIN 設定/変更/救済（紐づけ済み端末）。
  onSetPin: async (pin: string): Promise<AuthResult> => lobby.setPin(nickname, pin),
  // 管理メニュー（サーバーが pw を毎回照合）。
  onAdminList: (pw) => lobby.adminList(pw),
  onAdminDelete: (pw, name) => lobby.adminDelete(pw, name),
  onAdminEdit: (pw, name, patch) => lobby.adminEdit(pw, name, patch),
  onRematch: () => {
    if (!netConnected || spectating || ruleState.phase !== 'gameOver') return;
    localWantRematch = true;
    if (cpuMatch) {
      // CPU 戦は相手の合意不要 = 即座に doRematch（新規 race 再開）。
      opponentWantRematch = true;
      doRematch();
    } else {
      transport?.send({ t: 'rematch' });
      if (opponentWantRematch) doRematch();
      else syncRuleHud();
    }
  },
  onLeaveMatch: () => {
    if (!netConnected) return;
    // CPU 戦は transport が null（cpuMatch=true）なので送信不要。
    if (!cpuMatch) transport?.send({ t: 'leave' });
    // 自発的なロビー帰還を明示（試合は無効・記録なし）。
    controlsApi.showMatchEvent('ソロに戻りました', '↩ 対戦を退出しました', 'leave');
    teardownMatch();
  },
  onChat: (text: string) => {
    if (!netConnected) return; // 観戦者も送信可（spectating でも許可）
    if (cpuMatch) return; // CPU 戦はチャット送信無効（受信側がいない）
    const name = nickname || (spectating ? '観戦者' : 'あなた');
    // 自分の音声プロファイルを同梱し、相手はこれで読み上げる（「相手は相手の設定に従う」）。
    const voice = chatTts.getMyVoiceProfile();
    transport?.send({ t: 'chat', text, name, voice });
    controlsApi.showChatMessage(text, 'me', name); // 自分側の表示（名前付き・自分のプロファイルで読み上げ）
  },
  // （旧）CPU への直接挑戦リクエスト。§7A 指名マッチ移行後は OpponentPicker 経由で
  // CPU を選ぶフローへ統合済み（onOpponentPickerSubmit 経由）。本ハンドラは
  // 互換のため残置するが、新 UI から呼ばれることはない。
  onChallengeCpu: () => {
    if (netConnected) return; // 既に対戦/観戦中は無視
    pendingRaceStart = { kind: 'cpu' };
    pendingCpuPlayer = getCpuPlayers()[0] ?? null;
    controlsApi.showRaceModal({
      opponentName: pendingCpuPlayer?.displayName ?? 'CPU',
      vsCpu: true,
    });
  },
  // race 選択モーダルの「対戦開始」: CPU 戦のみが本ハンドラを通る（Step 4 で controls.ts が分岐済み）。
  // 人間対戦は onRaceConfigChange → lobby.acceptRace → onRaceStart 受信で startMatch を呼ぶ。
  onRaceStart: (params: { readonly targetWins: 1 | 3 | 5 | 9; readonly breakRule: 'alternate' | 'winner' }) => {
    const pending = pendingRaceStart;
    pendingRaceStart = null;
    if (!pending || pending.kind !== 'cpu') return;
    const cpu = pendingCpuPlayer ?? getCpuPlayers()[0];
    pendingCpuPlayer = null;
    if (!cpu) return; // CPU 定義が 0 件なら開始しない（防御）
    startCpuMatch(params.targetWins, params.breakRule, cpu);
  },
  // §7A: 相手選択モーダルの「対戦を申し込む」押下
  onOpponentPickerSubmit: (selectedUserId: string) => {
    // localStorage から前回設定を復元（無ければデフォルト）。
    const raceA = loadLastRaceConfig();
    // requestMatch 送信（CPU 特例は lobby 側で短絡処理される＝onPickAccepted がローカル即発火）
    lobby.requestMatch(selectedUserId, raceA);
  },
  // §7A: 相手選択モーダルの「キャンセル」押下
  onOpponentPickerCancel: () => {
    amWaiting = false;
    backgroundWaiting = false;
    controlsApi.setBackgroundWaitingMode(false);
    lobby.setWaiting(false);
    controlsApi.closeOpponentPicker();
    controlsApi.closeMatchInvitation();
    clearMatchInvitationNotified();
    void wakeLock.release();
    syncLobbyView();
  },
  // 「別の人が来るまで待機する」ボタン押下: バックグラウンド待機モードのトグル。
  // active=true: モーダル閉じ＋ waiting 維持（ソロプレイへ復帰可能）。
  // active=false: モーダル閉じ＋ waiting 解除（実質キャンセル相当）。
  onToggleBackgroundWaiting: (active: boolean) => {
    if (active) {
      // 背景待機モード ON: amWaiting/lobby.setWaiting は既に true のはず（picker 起動時に true）
      amWaiting = true;
      backgroundWaiting = true;
      lobby.setWaiting(true);
      controlsApi.setBackgroundWaitingMode(true);
      // Wake Lock は acquire 済みのまま維持（人が来た時の通知を逃さないため）。
    } else {
      // 背景待機モード OFF（解除）: waiting も解除する
      amWaiting = false;
      backgroundWaiting = false;
      lobby.setWaiting(false);
      controlsApi.setBackgroundWaitingMode(false);
      controlsApi.closeMatchInvitation();
      clearMatchInvitationNotified();
      void wakeLock.release();
    }
    syncLobbyView();
  },
  // 対戦勧誘ダイアログで「対戦する」押下: CPU 戦中なら teardown、その後 requestMatch を発行。
  onAcceptMatchInvitation: (opponentUserId: string) => {
    // 当該 userId は通知済みリストから削除（成立すれば不要・失敗時は次の roster 更新で再評価）。
    notifiedOpponentIds.delete(opponentUserId);
    const wasCpuMatch = cpuMatch;
    if (wasCpuMatch) {
      // CPU 戦を即終了（race 破棄・思考タイマー解除）して対人戦へ切替。
      controlsApi.showMatchEvent('対人戦へ切替', '↩ CPU 戦を退出しました', 'leave');
      teardownMatch();
    }
    // requestMatch 発行 → 既存 onPickAccepted フローで race モーダルが開く。
    // 待機状態は維持（対戦成立時に startMatch 内で setWaiting(false)）。
    amWaiting = true;
    backgroundWaiting = false; // ピッカーは表示しないが背景待機状態は解除（race モーダルへ遷移するため）
    controlsApi.setBackgroundWaitingMode(false);
    lobby.setWaiting(true);
    const raceA = loadLastRaceConfig();
    lobby.requestMatch(opponentUserId, raceA);
    syncLobbyView();
  },
  // 対戦勧誘ダイアログで「今は続ける」押下: 通知済み Set に追加（同一 userId は再通知しない）。
  onDismissMatchInvitation: (opponentUserId: string) => {
    notifiedOpponentIds.add(opponentUserId);
  },
  // §7B: ラック設定の値変更/承諾通知（リアルタイム双方向通知）
  onRaceConfigChange: (matchId: string, race: RaceConfig, accepted: boolean) => {
    lobby.updateRaceProposal(matchId, race, accepted);
    // 自分の選択は localStorage に保存（次回復元用）
    saveLastRaceConfig(race);
  },
  // §7B: ラック設定モーダルの「キャンセル」押下
  onRaceCancel: (matchId: string) => {
    // CPU 戦（matchId が cpu- プレフィックス）は lobby 側で no-op になる
    lobby.cancelProposal(matchId);
    controlsApi.closeRaceModal();
    // 相手選択モーダルへ戻る（waiting 維持）。候補を再構築して再オープン。
    if (amWaiting) {
      controlsApi.showOpponentPicker({ candidates: buildOpponentCandidates() });
    }
  },
  // §7B: ラック設定モーダル内チャット送信。
  // CPU 戦（matchId が cpu-）は lobby.sendLobbyChat 側で no-op になるため、ここでは matchId 判定をしない。
  // 自分側の echo 表示は main で名前解決して呼ぶ（既存対戦中チャットと同じパターン・nickname の出所を一元化）。
  onLobbyChatSend: (matchId: string, text: string) => {
    // 自分の音声プロファイルを同梱し、受信側がこれで読み上げる。
    const voice = chatTts.getMyVoiceProfile();
    lobby.sendLobbyChat(matchId, text, voice);
    const name = nickname || 'あなた';
    controlsApi.showLobbyChatInRaceModal(text, 'me', name);
  },
});

// ---- ソロ・ドリル（F-30）: セッション制御 ----
// ドリルを盤面へ読み込んで開始する。プラクティス基盤で動作（ルール無効・ターン交代/再ラックなし）。
// セッション保存についての契約: ドリル中は persist() が no-op になる（永続キーは playMode='practice'
// と「ドリル配置のスナップショット」で汚染しない方針）。これにより、ドリル中にタブを閉じても
// 次回起動時には「ドリル前の最後の保存状態」（モード・盤面）から復元される。
const startDrill = (layout: DrillLayout): void => {
  // ドリルに入る直前のモードを記憶（やり直し再ロードでは上書きしない＝最初の進入時のみ捕捉）。
  if (drillReturnMode === null) drillReturnMode = playMode;
  playMode = 'practice';
  rulesActive = false;
  const resolved = resolveDrill(layout);
  // 盤上の的玉を一旦すべて除去し、ドリルの的玉のみ配置する（手球 id=0 は残す）。
  for (const id of table.balls.map((bb) => bb.id)) {
    if (id !== 0) removeBall(table, id);
  }
  for (const rb of resolved.balls) addBall(table, rb.number, rb.x, rb.y);
  // 正確な位置へ（手球含む・clampToTable＋重なり解消は placeBallAt が担う）。
  placeBallAt(table, 0, resolved.cue.x, resolved.cue.y);
  for (const rb of resolved.balls) placeBallAt(table, rb.number, rb.x, rb.y);
  currentDrill = { layout, run: initDrillRun(layout), maxShots: drillMaxShots(layout) };
  shotActive = false;
  preShot = null;
  shotOutcomes = [];
  controlsApi.setDrillLock(true); // 配置・球追加・ドラッグを禁止（固定配置を保証）
  rebuildBallViews();
  aimAtLowestBall();
  rig.reset(cue.aim.angle + Math.PI);
  drillPanel?.startSession(layout);
  // ※ persist() を呼ばない: ドリル中の盤面/モードを保存すると、リロード時に
  //   設定 UI（pool.playMode='nineBall' 等）と保存セッション（mode='practice'＋
  //   ドリル盤面）の食い違いを起こす（2026-06-16 不具合）。
};

// 1 ショット静止後の合否反映（ショット上限制＝drill-pass-model）。
//   上限内クリア=達成（ミスありでも可）／ノーミスは称号／上限超過は最初からやり直し。
const handleDrillShot = (obs: ShotObservation): void => {
  if (!currentDrill) return;
  const { layout, maxShots } = currentDrill;
  const stepped = stepDrill(layout, obs, currentDrill.run, maxShots);
  currentDrill = { ...currentDrill, run: stepped.run };
  if (stepped.outcome === 'cleared' || stepped.outcome === 'clearedNoMiss') {
    const noMiss = stepped.outcome === 'clearedNoMiss';
    drillProgress = recordDrillClear(drillProgress, layout.id, noMiss);
    saveDrillProgress(drillProgress);
    // ログイン時はサーバー権威へ同期（未ログイン/ループバックは lobby 側で no-op）。
    if (serverStats && nickname.length > 0) {
      try {
        lobby.reportDrillClear(nickname, {
          drillId: layout.id,
          rank: layout.rank,
          noMiss,
        });
      } catch (err) {
        console.error('ドリルクリア送信失敗:', err);
      }
    }
    drillPanel?.showCleared(layout, noMiss);
    currentDrill = null; // セッション終了（盤面は保持・再挑戦は 🎯 から）
    return;
  }
  if (stepped.outcome === 'failed') {
    // ショット上限超過 → 最初からやり直し。失敗表示を見せてから盤面をリロードする。
    drillPanel?.showFailed(layout);
    currentDrill = null;
    const retry = layout;
    // 待機中にユーザーが「✕ 終了」や別ドリル開始をした場合はリロードしない
    //（終了で drillReturnMode=null、別ドリルで currentDrill!=null になる）。
    setTimeout(() => {
      if (currentDrill === null && drillReturnMode !== null) startDrill(retry);
    }, 1200);
    return;
  }
  // progress / miss: 続行。残球・ショット消費・ノーミス継続を表示。
  drillPanel?.updateGauge({
    remaining: stepped.run.remaining.length,
    total: layout.balls.length,
    shotsUsed: stepped.run.shotsUsed,
    maxShots,
    misses: stepped.run.misses,
  });
};

drillPanel = createDrillPanel({
  onStart: startDrill,
  getProgress: () => drillProgress,
  onExit: exitDrill,
});

// 現在のゲーム進行をセッションへ保存する（レジューム用）。
// 静止時のみ保存する＝復元対象は「直近の静止盤面」。飛行中の途中状態は保存しない
// （次フレームの収束を待たずに保存すると速度ゼロで固まった不正盤面になり得るため）。
// ドリル中はセッション汚染を避けるため保存しない（startDrill のコメント参照）。
const persist = (): void => {
  if (!isResting(table)) return;
  if (currentDrill !== null) return; // ドリル中はセッションを汚染しない（mode/balls の食い違い回避）
  const ps = controlsApi.getPersistState();
  saveSession({
    mode: playMode,
    balls: serializeBalls(table),
    broken: ps.broken,
    ballInHand: ps.ballInHand,
    runAlive: breakRun.isRunAlive(),
    // ルール適用モードのみルール状態を永続（手番/フェーズ/連続ファウル/勝敗）。
    rule: rulesActive ? ruleState : null,
  });
};

// バックグラウンド化・離脱の直前に保存（タブ破棄→完全リロードでも復元できる）。
registerPersistHooks(persist);

// ---- ルール HUD 整形 & 結果適用 ----
// 手番はニックネームで表示する。
// 観戦時は試合参加者（status='playing'）をロスターから引き、userId 昇順で
// host=P0 / guest=P1 に対応づける（loopback-lobby の tryPair が userId 昇順で
// host=[0]/guest=[1] を決めるため一致する）。ロスター未取得の枠は番号フォールバック。
const spectatedPlayers = (): readonly LobbyUser[] => {
  if (!spectateMatchId) return [];
  return lastRoster
    .filter((u) => u.status === 'playing' && u.matchId === spectateMatchId)
    .sort((a, b) => (a.userId < b.userId ? -1 : 1));
};
const playerTag = (p: 0 | 1): string => {
  if (spectating) {
    const name = spectatedPlayers()[p]?.nickname;
    return name && name.length > 0 ? name : p === 0 ? 'プレイヤー1' : 'プレイヤー2';
  }
  if (p === myPlayer) return nickname || 'あなた';
  return opponentNickname || '相手';
};
const FOUL_TEXT: Record<FoulReason, string> = {
  scratch: 'SCRATCH',
  noHit: 'NO HIT',
  wrongBall: 'WRONG BALL',
  noRail: 'NO RAIL',
  objectBallOffTable: 'BALL OFF TABLE',
  illegalBreak: 'ILLEGAL BREAK',
  doubleHit: 'DOUBLE HIT',
};

// 現在のルール状態＋直近結果を英語のステータス行へ整形する。
const formatRuleMessage = (): string => {
  // 再接続中はそれを最優先で表示。
  if (netConnected && netState === 'reconnecting') return '⚠ 再接続中…';
  const foulNote =
    lastRuleResult && lastRuleResult.foul
      ? `FOUL (${lastRuleResult.foulReasons.map((r) => FOUL_TEXT[r]).join(', ')}) · `
      : '';

  // ソロ（オフライン）: 手番(P1/P2)は出さない。ファウル/BIH/ブレイク/クリアのみ。
  if (!ruleParams.online) {
    if (ruleState.phase === 'gameOver') return 'RACK CLEARED';
    switch (ruleState.phase) {
      case 'breakPlacement':
        return 'BREAK';
      case 'ballInHand':
        return `${foulNote}BALL IN HAND`;
      default:
        // 通常進行中は何も出さない（HUD 非表示）。
        return foulNote ? foulNote.replace(/ · $/, '') : '';
    }
  }

  // オンライン2P: 手番をニックネームで表示。
  if (ruleState.phase === 'gameOver') {
    return `${playerTag(ruleState.winner ?? 0)} WINS`;
  }
  const turn = playerTag(ruleState.current);
  const warn = ruleState.players[ruleState.current].foulWarned ? ' · 2 FOULS' : '';
  switch (ruleState.phase) {
    case 'breakPlacement':
      return `${turn} · BREAK`;
    case 'pushOutResponse':
      return `${turn} · PUSH OUT?`;
    case 'ballInHand':
      return `${foulNote}${turn} · BALL IN HAND${warn}`;
    case 'pushOutOption':
    case 'inPlay':
      return `${foulNote}${turn}${warn}`;
    default:
      return turn;
  }
};

// ルール HUD を controls へ反映する。
const syncRuleHud = (): void => {
  controlsApi.setRuleView({
    active: rulesActive,
    phase: ruleState.phase,
    current: ruleState.current,
    pushOutDeclared: ruleState.pushOutDeclared,
    message: rulesActive ? formatRuleMessage() : '',
    gameOver: ruleState.phase === 'gameOver',
    online: netConnected,
    inputLocked: isInputLocked(),
    rematchRequested: localWantRematch,
  });
  // 手番（ruleState.current）の変化に合わせて active 側のキュー外観を切替。
  // syncRuleHud はルール遷移・配置遷移・接続変化等のあらゆる節目で呼ばれるため、
  // ここに 1 箇所追加するだけで「自分↔相手のキュー切替」が網羅される。
  applyActiveCueAppearance();
};

// 手玉を画面中心に据える構図へ確実に戻す（俯瞰/トップ/下見の各状態を解除）。
// ボールインハンド等で手玉が再配置された時、カメラがテーブル中心を向いたままに
// ならないようにする（camera-rig は overview/top/orbit が全て 0 かつ aiming のとき手玉を注視）。
const frameCueBall = (): void => {
  rig.clearOrbitTarget();
  rig.setOverview(false);
  rig.setFollow(false);
  rig.setAutoTopView(false);
  rig.setFraming(true);
  if (table.cueball.onTable()) {
    aimAtLowestBall();
    rig.alignToAim(cue.aim.angle);
  }
};

// reducer の結果を盤面・カメラ・配置モードへ反映する（HUD は syncRuleHud が担当）。
// ソロ（オフライン）で RACK CLEARED（gameOver）後、結果を少し見せてから自動で次ラック（ブレイク）へ。
const SOLO_RACK_RESET_DELAY_MS = 1500;
const scheduleSoloRackReset = (): void => {
  if (autoResetTimer !== null) return; // 多重予約を防ぐ
  autoResetTimer = window.setTimeout(() => {
    autoResetTimer = null;
    // 念のため: まだソロ gameOver のままなら次ラックへ（手動操作で状態が変わっていたら何もしない）。
    if (!ruleParams.online && ruleState.phase === 'gameOver') {
      resetRackAndAim('nineBall');
    }
  }, SOLO_RACK_RESET_DELAY_MS);
};

const applyRuleResult = (res: ShotResult): void => {
  if (res.nineRespotted) {
    spotBall(table, 9);
    rebuildBallViews();
  }
  const scratched = isCueBallScratched(table);
  if (scratched) respotCueBall(table); // 手球を盤上へ戻す（以後 BIH で自由配置）
  if (res.gameOver) {
    // ソロは RACK CLEARED を少し見せてから自動で次ラック（ブレイク）へ。
    // オンラインは盤をそのままに再戦合意/退出を待つ（自動リセットしない）。
    if (!ruleParams.online) scheduleSoloRackReset();
    return;
  }
  if (res.ballInHandAwarded) {
    frameCueBall(); // 手玉を中心に据える（再配置で視点がテーブル中心に逃げないように）
    controlsApi.enterBallInHand();
  } else if (table.cueball.onTable()) {
    aimAtLowestBall();
    rig.alignToAim(cue.aim.angle);
  }
};

// ---- 通信対戦: 接続・ハンドシェイク・同期 ----

// 受信した権威盤面・状態に合わせて、ローカルの配置モード／エイムを整える。
const syncOnlineControls = (): void => {
  if (table.cueball.onTable()) {
    aimAtLowestBall();
    rig.alignToAim(cue.aim.angle);
  }
  if (ruleState.current === myPlayer) {
    if (ruleState.phase === 'ballInHand') {
      frameCueBall(); // 自分のBIH: 手玉を中心に据える
      controlsApi.enterBallInHand();
    } else if (ruleState.phase === 'breakPlacement') {
      controlsApi.enterPlacement();
    }
  }
};

// 現在の照準＋手球位置を ShotPayload に写す（aim/shotStart で共用）。
const currentShotPayload = (): ShotPayload => ({
  cueX: table.cueball.pos.x,
  cueY: table.cueball.pos.y,
  angle: cue.aim.angle,
  power: cue.aim.power,
  offsetX: cue.aim.offset.x,
  offsetY: cue.aim.offset.y,
  elevation: cue.aim.elevation,
  squirt: getSquirt(),
});

// 相手ショットの権威確定（最終 snapshot＋ルール状態）を適用する。
const applyAuthoritative = (
  state: RuleState,
  balls: ReturnType<typeof serializeBalls>,
): void => {
  restoreBalls(table, balls);
  ruleState = state;
  rebuildBallViews();
  replayingRemote = false;
  awaitingAuthoritative = false;
  pendingAuthoritative = null;
  lastRuleResult = null;
  objBallsAtRest = objectBallsRemaining(table);
  rig.setOverview(false);
  rig.setFollow(false);
  rig.setAutoTopView(false);
  syncOnlineControls();
  syncRuleHud();
  maybeRecordResult();
};

// ラック勝敗確定（オンライン・観戦以外）を race 状態へ反映し、race 終了時のみ通算成績へ記録する。
// CPU 戦は recordResult/reportShot を呼ばない（集計対象外）。
const maybeRecordResult = (): void => {
  if (!netConnected || spectating) return;
  if (ruleState.phase !== 'gameOver' || recordedThisGame) return;
  recordedThisGame = true;
  const rackWinner = (ruleState.winner ?? 0) as 0 | 1;
  const myRackWon = rackWinner === myPlayer;
  // race 状態を更新（永続化なし）。
  if (currentRace) {
    currentRace = rackEnded(currentRace, rackWinner);
    syncRaceScore();
  }
  // race 継続中（未到達）はラック確定バナーのみ表示し、次ラックは rematch フローへ。
  if (currentRace && !currentRace.finished) {
    controlsApi.showMatchEvent(
      myRackWon ? '🏁 ラック獲得' : '🏳 ラックを落とした',
      `R${currentRace.rackIndex - 1} → ${formatScoreBanner(currentRace)} ・ 次のブレイク: ${currentRace.breaker === myPlayer ? 'あなた' : currentRace.opponentName}`,
      myRackWon ? 'win' : 'lose',
    );
    // CPU 戦は自動で次ラックへ。人間戦は既存の rematch 合意フロー（onRematch）に従う。
    if (cpuMatch) {
      window.setTimeout(() => continueCpuRace(), 1600);
    } else {
      // 人間戦: 即座に rematch を自動承諾し次ラックへ（race 中は rematch 合意を省略）。
      // タイマー ID を保持し teardownMatch で解除する（バナー表示中に相手が退出すると、
      // teardown 後にこのタイマーが発火してソロ復帰済みの盤面を勝手に再ラックしていた）。
      raceContinueTimer = window.setTimeout(() => {
        raceContinueTimer = 0;
        // teardown 後の遅延発火ガード: 対戦が既に終了していたら何もしない。
        if (!netConnected || !currentRace || currentRace.finished) return;
        localWantRematch = true;
        opponentWantRematch = true;
        recordedThisGame = false;
        doRematch();
      }, 1400);
    }
    syncLobbyView();
    return;
  }
  // race 終了（または race なし）= 通算成績へ反映（CPU 戦は除く）。
  const won = currentRace ? currentRace.winner === myPlayer : myRackWon;
  // race の総ラック数（rackEnded で rackIndex は次ラック番号にインクリメントされているため -1）。
  const racksPlayed = currentRace ? Math.max(0, currentRace.rackIndex - 1) : 1;
  if (!cpuMatch) {
    // 人間対戦の最終結果。targetWins/racksPlayed をサーバー集計に渡す（解錠条件の humanRaceWins 等で参照）。
    const tw = currentRace?.targetWins;
    recordResult(won, {
      ...(tw !== undefined ? { targetWins: tw } : {}),
      racksPlayed,
    });
  }
  // Perfect Game 判定: race を全勝（targetWins ラック全て自分のブレイクラン）で勝利。
  // 達成時は専用バナー + 背景に花火を打ち上げる。
  const perfect =
    !!currentRace &&
    won &&
    currentRace.targetWins === raceBreakRunByMe;
  // CPU 撃破イベントをサーバーへ送信（CPU 戦勝利時のみ）。
  // serverStats=false（未ログイン or ループバック）なら lobby 側で no-op になる。
  if (cpuMatch && won && currentRace && currentCpuPlayer) {
    try {
      lobby.reportCpuDefeat(nickname, {
        cpuId: currentCpuPlayer.id,
        targetWins: currentRace.targetWins,
        perfect,
        myBreakRuns: raceBreakRunByMe,
      });
    } catch (err) {
      console.error('CPU 撃破イベント送信失敗:', err);
    }
  }
  if (perfect) {
    fireFireworks();
    controlsApi.showMatchEvent(
      'Perfect Game!',
      cpuMatch
        ? `vs ${currentCpuPlayer?.displayName ?? 'CPU'}  全${currentRace!.targetWins}ラックBREAK&RUN`
        : `vs ${opponentNickname || '相手'}  全${currentRace!.targetWins}ラックBREAK&RUN`,
      'win',
    );
  } else {
    // 中央バナーで勝敗を強調（小トーストよりも見落としにくい）。
    controlsApi.showMatchEvent(
      won ? '🏆 勝利' : '☠ 敗北',
      cpuMatch
        ? `vs ${currentCpuPlayer?.displayName ?? 'CPU'}  ${currentRace ? formatScoreBanner(currentRace) : ''}`
        : won
          ? `vs ${opponentNickname || '相手'}  通算 ${matchStats.wins}勝${matchStats.losses}敗`
          : `vs ${opponentNickname || '相手'}  通算 ${matchStats.wins}勝${matchStats.losses}敗`,
      won ? 'win' : 'lose',
    );
  }
  syncLobbyView();
  // 人間対戦の試合終了時はロビーを自動オープン（次の相手を探しやすく）。
  // CPU 戦は他に対戦相手がいないためロビーを開かず、再戦ボタンを見やすくする
  // （UI 重なり防止＋ユーザーの次アクションを明確化）。
  if (!cpuMatch) controlsApi.openLobby();
};

// race スコアバナー用の "あなた X / 相手 Y / 先取N" 整形。
const formatScoreBanner = (r: RaceState): string => {
  const my = myPlayer === 0 ? r.winsP0 : r.winsP1;
  const opp = myPlayer === 0 ? r.winsP1 : r.winsP0;
  return `${my} - ${opp} / 先取${r.targetWins}`;
};

// CPU 戦で次ラックを開始する（race 継続時のみ）。
const continueCpuRace = (): void => {
  if (!cpuMatch || !currentRace || currentRace.finished) return;
  // 次ラックのブレイク権は race 状態の breaker に従う。
  const nextBreaker: PlayerId = currentRace.breaker;
  ruleState = initRuleState({ breaker: nextBreaker });
  recordedThisGame = false;
  resetRackAndAim('nineBall');
  syncRuleHud();
  syncRaceScore();
};

// CPU ショットプレビューのアニメーション設定。
// 対人戦と同等の「相手の構え→撞点設定→パワー→発射」を演出する。
const CPU_PREVIEW_TOTAL_MS = 1200; // プレビュー総時間
const CPU_PREVIEW_AIM_END = 0.45; // 0〜0.45: aim 回転
const CPU_PREVIEW_SPIN_START = 0.30;
const CPU_PREVIEW_SPIN_END = 0.70; // 0.30〜0.70: 撞点移動
const CPU_PREVIEW_POWER_START = 0.65; // 0.65〜1.0: パワー伸長

/** ease-in-out（滑らかな加減速）。 */
const easeInOut = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

/** 線形補間。 */
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** CPU ショットプレビューを 1.2 秒間アニメ表示する Promise。完了で resolve。
 *  ジッタ対策: 既存の animate() ループと同じ RAF 周期に乗せるため performance.now で
 *  経過時刻を計算するが、フレーム間で大きな値変化があると視覚的に飛ぶため、
 *  最初の applyAimPreview を **同期的に** 1回打ってフレーム同期させる。 */
const animateCpuShotPreview = (target: {
  readonly angle: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly power: number;
  readonly elevation: number;
}): Promise<void> => {
  const startAngle = cue.aim.angle;
  const startOX = cue.aim.offset.x;
  const startOY = cue.aim.offset.y;
  // 起点状態を即座に 1 回流す（pad が次フレームの tick() で表示されてから dot が
  // 中央に化けるのを防ぐ＝最初の値で applyAimPreview→renderPadDot 順序を確立）。
  controlsApi.applyAimPreview({
    angle: startAngle,
    offsetX: startOX,
    offsetY: startOY,
    power: 0,
    elevation: target.elevation,
  });
  const startTime = performance.now();
  return new Promise<void>((resolve) => {
    const tick = (): void => {
      // 対戦中断・状態変化で中止（即座に解決し、CPU 発射処理を回さない）。
      if (!cpuMatch || ruleState.current !== 1) {
        resolve();
        return;
      }
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / CPU_PREVIEW_TOTAL_MS);
      // 各フェーズの進捗（0..1 にクランプ）。
      const aimT = Math.min(1, Math.max(0, t / CPU_PREVIEW_AIM_END));
      const spinT = Math.min(
        1,
        Math.max(0, (t - CPU_PREVIEW_SPIN_START) / (CPU_PREVIEW_SPIN_END - CPU_PREVIEW_SPIN_START)),
      );
      const powerT = Math.min(1, Math.max(0, (t - CPU_PREVIEW_POWER_START) / (1 - CPU_PREVIEW_POWER_START)));
      // 短経路で角度補間（-π〜π の差で正規化）。
      let dAngle = target.angle - startAngle;
      while (dAngle > Math.PI) dAngle -= 2 * Math.PI;
      while (dAngle < -Math.PI) dAngle += 2 * Math.PI;
      controlsApi.applyAimPreview({
        angle: startAngle + dAngle * easeInOut(aimT),
        offsetX: lerp(startOX, target.offsetX, easeInOut(spinT)),
        offsetY: lerp(startOY, target.offsetY, easeInOut(spinT)),
        power: target.power * easeInOut(powerT),
        elevation: target.elevation,
      });
      if (t < 1) requestAnimationFrame(tick);
      else resolve();
    };
    requestAnimationFrame(tick);
  });
};

// CPU の手番を駆動する: 静止＋CPU 手番＋未スケジュール時に「思考時間」後に発射する。
// 既にスケジュール済み(cpuThinkTimer != 0)・対戦終了・観戦中は何もしない。
const maybeTriggerCpuTurn = (): void => {
  if (!cpuMatch) return;
  if (cpuThinkTimer !== 0) return;
  if (!isResting(table)) return;
  if (ruleState.phase === 'gameOver') return;
  // CPU は P1。自分（myPlayer=0）の手番では何もしない。
  if (ruleState.current !== 1) return;
  // 「盤面を見ている」演出時間。カメラがトップビュー→構え視点へ補間中に planShot が
  // メインスレッドをブロック（300-1500ms）するとカメラ回転が途中で停止して見える。
  // そこで充分長い時間（1.6-2.0s）を空けてカメラ補間を完了させ、その後に planShot を実行。
  // 完了後の preview 直前は短い「考えてる」演出（300ms）を入れて自然な間合いに。
  const thinkMs = 1600 + Math.floor(Math.random() * 400); // 1.6〜2.0 秒
  cpuThinkTimer = window.setTimeout(async () => {
    cpuThinkTimer = 0;
    if (!cpuMatch || ruleState.current !== 1 || !isResting(table)) return;
    // ここで planShot ブロッキングが発生するが、カメラはすでに静止しているため
    // ユーザーから見るとカメラのジッタは生じない（思考時間として認識される）。
    // currentCpuPlayer のスタイルで思考（fallback は 'tricky'＝旧 Phase 1 互換）。
    // cpuId を planShot/planBallInHand に渡すことでプレイヤー個別の localStorage オーバーレイが反映される。
    const style = currentCpuPlayer?.style ?? 'tricky';
    const cpuId = currentCpuPlayer?.id;

    // Push-Out Option: ブレイク直後の最初のショット権者は「プッシュアウト宣言→ノールール撞き」可能。
    // 明白なポットがなければ宣言してラグ風ショットでお返しする（実戦的なプッシュアウト戦略）。
    if (ruleState.phase === 'pushOutOption' && !ruleState.pushOutDeclared) {
      if (!cpuHasClearPot(table, style)) {
        try {
          ruleState = dispatch(ruleState, { type: 'declarePushOut' }, ruleParams).state;
          syncRuleHud();
        } catch (err) {
          console.error('CPU プッシュアウト宣言に失敗:', err);
        }
      }
    }

    // Ball-In-Hand: ファウル後など手玉が自由配置できる場合は、最良の配置を選んで手玉を移動してから撞く。
    // planBallInHand は内部で「候補位置に手玉を一時配置→ planShot →最良ピック → 元位置に復元」を行う。
    // 採用された配置位置に手玉を本番配置してから plan を実行する。
    let plan: ReturnType<typeof cpuPlanShot> = null;
    if (ruleState.phase === 'ballInHand') {
      const bih = cpuPlanBih(table, style, cpuId);
      if (bih) {
        placeBallAt(table, 0, bih.placeX, bih.placeY);
        rebuildBallViews();
        frameCueBall(); // 視点を新しい手玉位置へ寄せる
        plan = bih.plan;
      } else {
        // フォールバック: 候補生成失敗時は現在位置のまま planShot。
        plan = cpuPlanShot(table, style, cpuId);
      }
    } else if (ruleState.pushOutDeclared) {
      // プッシュアウト中はラグ風ショット（ルール無効）。スクラッチを避けつつ手玉を的玉付近へ。
      plan = cpuPlanPushOut(table);
    } else {
      plan = cpuPlanShot(table, style, cpuId);
    }
    if (!plan) {
      console.warn('CPU: 思考結果なし（フォールバック: ノーアクション）');
      return;
    }
    // プレビュー アニメ: 対人戦の 'aim' メッセージ相当を内部で発火し、構えを可視化。
    await animateCpuShotPreview({
      angle: plan.shot.angle,
      offsetX: plan.shot.offsetX,
      offsetY: plan.shot.offsetY,
      power: plan.shot.power,
      elevation: plan.shot.elevation,
    });
    // 中断チェック（プレビュー中に teardownMatch 等が走った場合）。
    if (!cpuMatch || ruleState.current !== 1 || !isResting(table)) return;
    // プレビュー解除（パワーリセット）→ 打前観測 → 物理発火。
    controlsApi.clearAimPreview();
    if (rulesActive) {
      preShot = beginShotObservation(table);
      pendingDoubleHit = false;
      analyticsCtx = beginAnalytics(table);
      analyticsPhaseBefore = ruleState.phase;
    }
    // CPU のショット: ショット中だけトップビューを強制するフラグを立てる。
    shotByCpu = true;
    // 物理エンジンへ撞き発火（ShotParams は world.shoot と同じ型）。
    if (!shoot(table, plan.shot)) {
      console.warn('CPU: shoot 失敗（既に動球あり？）');
      preShot = null;
      shotByCpu = false; // 発火失敗時はフラグ戻す
    }
  }, thinkMs);
};

// 再戦（両者合意）。host が新ラックを作って同期。
// race-to-N が終了済みの場合は新規 race として再開、進行中の場合は次ラックへ。
const doRematch = (): void => {
  localWantRematch = false;
  opponentWantRematch = false;
  recordedThisGame = false;
  // race 終了済み = 新規 race（同じ targetWins/breakRule で再スタート）。
  if (currentRace && currentRace.finished) {
    const opts = {
      targetWins: currentRace.targetWins,
      breakRule: currentRace.breakRule,
      opponentName: currentRace.opponentName,
    };
    if (cpuMatch) {
      // CPU 戦: 同じ CPU と再戦（startCpuMatch が race を初期化）。
      // currentCpuPlayer は再戦中も保持されている前提（teardown 時のみ null）。
      const cpu = currentCpuPlayer ?? getCpuPlayers()[0];
      if (!cpu) return;
      startCpuMatch(opts.targetWins, opts.breakRule, cpu);
      return;
    }
    // 人間戦 host: 新規 race と新ラックで再開、guest へ init で同期。
    currentRace = initRaceState({
      targetWins: opts.targetWins,
      breakRule: opts.breakRule,
      firstBreaker: 0,
      vsCpu: false,
      opponentName: opts.opponentName,
    });
    raceBreakRunByMe = 0;
    if (myPlayer === 0) {
      ruleState = initRuleState({ breaker: 0 });
      resetRackAndAim('nineBall');
      transport?.send({ t: 'init', state: ruleState, balls: serializeBalls(table) });
    }
    syncRuleHud();
    syncRaceScore();
    return;
  }
  // race 継続中 = 次ラックへ（breaker は currentRace.breaker に従う）。
  const fallbackBreaker: PlayerId = ruleState.breaker === 0 ? 1 : 0;
  const nextBreaker: PlayerId = currentRace ? currentRace.breaker : fallbackBreaker;
  if (myPlayer === 0) {
    ruleState = initRuleState({ breaker: nextBreaker });
    resetRackAndAim('nineBall');
    transport?.send({ t: 'init', state: ruleState, balls: serializeBalls(table) });
  }
  syncRuleHud();
  syncRaceScore();
};

const onNetMessage = (msg: NetMessage): void => {
  // 受信＝相手生存。再接続中だったら復帰。
  lastPeerActivity = Date.now();
  if (netState !== 'connected') {
    netState = 'connected';
    syncRuleHud();
  }
  switch (msg.t) {
    case 'ping':
      return; // 生存確認のみ
    case 'condition': {
      applyExternalHumidity(msg.humidity);
      controlsApi.showToast('相手がテーブルコンディションを変更しました');
      return;
    }
    case 'rematch': {
      opponentWantRematch = true;
      if (localWantRematch) {
        doRematch();
      } else {
        controlsApi.showToast('相手が再戦を希望しています');
        syncRuleHud();
      }
      return;
    }
    case 'leave': {
      controlsApi.showMatchEvent(
        '相手が退出',
        `↩ ${opponentNickname || '相手'} がロビーへ戻りました`,
        'leave',
      );
      teardownMatch();
      return;
    }
    case 'chat': {
      // 他者からのチャット（プレイヤー/観戦者）。送信者名付きで表示。本文は controls 側で正規化・textContent描画。
      // voice が同梱されていれば「相手の設定に従う」読み上げを行う（無ければ既定）。
      if (typeof msg.text === 'string') {
        controlsApi.showChatMessage(
          msg.text,
          'opponent',
          typeof msg.name === 'string' ? msg.name : undefined,
          msg.voice,
        );
      }
      return;
    }
    case 'init': {
      // ゲスト: ホストの初期盤面・状態を適用。
      playMode = 'nineBall';
      rulesActive = true;
      netConnected = true;
      ruleParams = { ...ruleParams, online: true };
      resetWorld(table, 'nineBall');
      restoreBalls(table, msg.balls);
      rebuildBallViews();
      ruleState = msg.state;
      objBallsAtRest = objectBallsRemaining(table);
      syncOnlineControls();
      syncRuleHud();
      return;
    }
    case 'aim': {
      // 相手の構え（照準）を反映。観戦側でキュー描画＋カメラ追従。自分の手番なら無視。
      if (!spectating && ruleState.current === myPlayer) return;
      const s = msg.shot;
      cue.aim.angle = s.angle;
      cue.aim.offset.set(s.offsetX, s.offsetY, 0);
      cue.aim.elevation = s.elevation;
      cue.aim.power = s.power;
      placeBallAt(table, 0, s.cueX, s.cueY); // ボールインハンド/ブレイク配置の追従
      // カメラは追従させない（観戦側は自由に見回せる）。初期フレーミングは
      // 手番切替時の syncOnlineControls が一度だけ行う。
      return;
    }
    case 'shotStart': {
      // 相手のショット開始: 撞く直前の全球を復元（再生開始の基準）。
      awaitingAuthoritative = false;
      pendingAuthoritative = null;
      restoreBalls(table, msg.balls);
      rebuildBallViews();
      replayingRemote = true;
      if (isRemoteNet()) {
        // 遠隔（端末差あり）: 物理再生せず、track の補間再生で見た目を権威と一致させる。
        remotePlayActive = true;
        remotePlayStartMs = wallMs;
        trackPlayer = createTrackPlayer();
      } else {
        // ループバック（同一エンジン・決定論一致）: 従来どおり同じ撞き方でローカル物理再生。
        const s = msg.shot;
        const ok = shoot(table, {
          angle: s.angle,
          power: s.power,
          offsetX: s.offsetX,
          offsetY: s.offsetY,
          elevation: s.elevation,
        });
        if (ok) {
          applySquirt(
            table.cueball.vel,
            s.offsetX,
            s.power * Math.cos(s.elevation),
            s.squirt,
          );
        }
      }
      if (useAutoTopForShot()) rig.setAutoTopView(true);
      else rig.setOverview(true);
      syncRuleHud();
      return;
    }
    case 'track': {
      // 遠隔再生: 届いた軌跡サンプルをプレイヤーへ蓄積（animate で補間再生）。
      if (trackPlayer) trackPlayer.push(msg.s);
      return;
    }
    case 'shotEnd': {
      // 権威確定。再生が既に静止していれば即補正、まだなら静止時に補正。
      pendingAuthoritative = { state: msg.state, balls: msg.balls };
      if (awaitingAuthoritative) applyAuthoritative(msg.state, msg.balls);
      return;
    }
    case 'state': {
      // 非ショットの状態変化（プッシュアウト宣言/応答）。
      ruleState = msg.state;
      restoreBalls(table, msg.balls);
      syncOnlineControls();
      syncRuleHud();
      return;
    }
  }
};

// ロビー表示用: アイコン色（緑=待機者あり / 青=対戦中あり / グレー=なし）。
// CPU は常時 waiting 扱いなので緑判定から除外（CPU しかいない時に緑で目立つのを避ける）。
// 人間ユーザーが入った時のみ緑になる挙動にする。
const computeIconState = (): 'gray' | 'green' | 'blue' => {
  const humans = lastRoster.filter(
    (u) => u.userId !== lobby.userId && !isCpuUserId(u.userId),
  );
  if (humans.some((u) => u.status === 'waiting')) return 'green';
  if (humans.some((u) => u.status === 'playing')) return 'blue';
  return 'gray';
};
// 順位リストの行を構築する: 永続リーダーボード（オフライン含む）＋在席ロスター（オンライン状態）を
// 名前キーでマージし、勝率→試合数→最終オンライン→名前で整列して Pos を付与する。
const buildLobbyRows = (): LobbyRowView[] => {
  const canSpectate = !netConnected; // 対戦/観戦中は新規観戦しない
  // 名前 → 成績（リーダーボード = サーバー権威）。
  // isAdmin はサーバー側で ADMIN_NAMES に該当した場合のみ true で同梱される（未指定=通常）。
  const statsByName = new Map<
    string,
    { wins: number; losses: number; lastSeen: number; isAdmin?: boolean }
  >();
  for (const e of leaderboard) {
    statsByName.set(e.name, {
      wins: e.wins,
      losses: e.losses,
      lastSeen: e.lastSeen,
      ...(e.isAdmin ? { isAdmin: true } : {}),
    });
  }
  // 名前 → 在席情報（オンライン状態）。同名は後勝ち（重複名は許容仕様）。
  const onlineByName = new Map<string, LobbyUser>();
  for (const u of lastRoster) {
    if (u.nickname.length > 0) onlineByName.set(u.nickname, u);
  }
  // 対象の名前集合（リーダーボード ∪ 在席 ∪ 自分）。
  const names = new Set<string>([...statsByName.keys(), ...onlineByName.keys()]);
  if (nickname.length > 0) names.add(nickname);

  const rows: LobbyRowView[] = [];
  for (const name of names) {
    const isSelf = name === nickname && nickname.length > 0;
    const ru = onlineByName.get(name);
    const lb = statsByName.get(name);
    // 成績: 自分はローカル/サーバー権威の matchStats、他者はリーダーボード優先、
    // 無ければ presence 同梱の自己申告（ループバック用）、それも無ければ 0/0。
    const wins = isSelf ? matchStats.wins : lb?.wins ?? ru?.stats?.wins ?? 0;
    const losses = isSelf ? matchStats.losses : lb?.losses ?? ru?.stats?.losses ?? 0;
    const online = ru !== undefined;
    const isCpu = ru !== undefined && isCpuUserId(ru.userId);
    // 管理人フラグ: leaderboard 同梱 isAdmin（サーバー権威）または自分が管理人としてログイン中（matchStats.isAdmin）。
    const isAdmin =
      lb?.isAdmin === true || (isSelf && inventory.isAdmin());
    rows.push({
      pos: null, // 整列後に付与
      name,
      wins,
      losses,
      lastSeen: lb?.lastSeen ?? 0,
      online,
      status: online ? ru.status : 'offline',
      isSelf,
      matchId: ru?.matchId ?? null,
      canSpectate: canSpectate && !isSelf && online && ru.status === 'playing' && !isCpu,
      isCpu,
      ...(isAdmin ? { isAdmin: true } : {}),
    });
  }

  // 整列: ①ランク対象(3試合以上)を勝率降順→試合数降順→最終オンライン新しい順→名前昇順、
  //        ②ランク外を試合数降順→最終オンライン新しい順→名前昇順、で後ろに。
  //        CPU 行は順位付与対象外で先頭に固定（強調）。
  const games = (r: LobbyRowView): number => r.wins + r.losses;
  const rate = (r: LobbyRowView): number => (games(r) > 0 ? r.wins / games(r) : 0);
  const ranked = (r: LobbyRowView): boolean => games(r) >= 3 && !r.isCpu;
  rows.sort((a, b) => {
    if (a.isCpu !== b.isCpu) return a.isCpu ? -1 : 1; // CPU 先頭
    if (ranked(a) !== ranked(b)) return ranked(a) ? -1 : 1;
    if (ranked(a) && rate(a) !== rate(b)) return rate(b) - rate(a);
    if (games(a) !== games(b)) return games(b) - games(a);
    if (a.lastSeen !== b.lastSeen) return b.lastSeen - a.lastSeen;
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  });
  // Pos はランク対象のみ連番付与（CPU は付与せず・controls 側で 'P' 表示）。
  let pos = 0;
  return rows.map((r) => (ranked(r) ? { ...r, pos: ++pos } : r));
};
const syncLobbyView = (): void => {
  controlsApi.setLobbyView({
    nickname,
    waiting: amWaiting,
    iconState: computeIconState(),
    rows: buildLobbyRows(),
    inMatch: netConnected, // 対戦/観戦中は相手募集を無効化
  });
};

/** 解錠条件の CPU 表示名を動的解決する（生の cpuId をユーザー表示しない・改名にも追従）。 */
const cpuDisplayName = (cpuId: string): string => getCpuPlayer(cpuId)?.displayName ?? cpuId;

/** 解錠条件を短い日本語ヒントへ整形（未取得アイテムの未取得理由を表示するため）。 */
// ランク文字列の表示ラベル（'C'→'C級' 等）。未知のランクはそのまま返す（ハイント表示用）。
const drillRankLabel = (rank: string): string =>
  DRILL_RANKS.find((d) => d.rank === rank)?.label ?? rank;

/** アイテム難易度スコア（小さいほど易しい＝低性能・大きいほど難しい＝高性能）。
 *  ユーザー要件: 「アイテムの性能が高いものが難易度が高い」。**効果値（ItemEffects）の合成**で
 *  単一の連続スコアを作り、性能順≡難易度順の単調関係を保証する。
 *
 *  重み付け方針:
 *   - `autoAimMatchMaxCutDeg`（° → ×15pt）: 自動照準のカット角は単調に強力（5° → 75pt / 75° → 1125pt）。
 *   - `breakShotMaxSpeed`（m/s → ×30pt）: ブレイク最大速度は単調に強力（9.17 → 275 / 11.11 → 333）。
 *     ※ 既定エンジン値以下でも 0 にせず、装備差分を素直に並べる（drill-break-cue=33km/h は
 *        carbon-cue=40km/h より弱いという序列を維持）。
 *   - `showAimGuideInMatch`: 軌跡ガイド表示は独立効果（+300pt）。
 *   - `admin-only`: 一般プレイヤーには無関係なので最後（999999）。
 *
 *  解錠条件（unlock）は無関係＝報酬経路（CPU vs ドリル）を跨いで「より強い装備が後ろ」になる。 */
const unlockDifficulty = (def: ReturnType<typeof getItemDefinition>): number => {
  if (!def) return Number.POSITIVE_INFINITY;
  if (def.unlock.type === 'admin-only') return 999999;
  const e = def.effects;
  let score = 0;
  if (typeof e.autoAimMatchMaxCutDeg === 'number') {
    score += e.autoAimMatchMaxCutDeg * 15;
  }
  if (typeof e.breakShotMaxSpeed === 'number') {
    score += e.breakShotMaxSpeed * 30;
  }
  if (e.showAimGuideInMatch) {
    score += 300;
  }
  return score;
};

const formatUnlockHint = (def: ReturnType<typeof getItemDefinition>): string => {
  if (!def) return '';
  const u = def.unlock;
  switch (u.type) {
    case 'cpu-defeat':
      return `${cpuDisplayName(u.cpuId)} を倒すと入手`;
    case 'cpu-defeat-race':
      return `${cpuDisplayName(u.cpuId)} を 先取${u.minTargetWins} 以上で倒すと入手`;
    case 'cpu-defeat-perfect':
      return u.minTargetWins
        ? `${cpuDisplayName(u.cpuId)} に 先取${u.minTargetWins} を全マスワリ（Perfect Game）で勝利すると入手`
        : `${cpuDisplayName(u.cpuId)} を Perfect Game で倒すと入手`;
    case 'cpu-defeat-count':
      return `${u.minCount} 人以上の CPU を倒すと入手`;
    case 'human-match-played':
      return `対人戦 ${u.minMatches} 試合プレイで入手`;
    case 'human-wins':
      return `対人戦 ${u.minWins} 勝で入手`;
    case 'human-race-wins':
      return `先取${u.minTargetWins} 以上のレースで ${u.minWins} 勝で入手`;
    case 'streak':
      return `連続BREAK&RUN ${u.minStreak} 回達成で入手`;
    case 'rack-total':
      return `累計 ${u.minRacks} ラックプレイで入手`;
    case 'drill-cleared': {
      const d = DRILLS.find((x) => x.id === u.drillId);
      return d ? `ドリル「${d.name}」をクリアすると入手` : 'ドリルクリアで入手';
    }
    case 'drill-no-miss': {
      const d = DRILLS.find((x) => x.id === u.drillId);
      return d ? `ドリル「${d.name}」をノーミスで取り切ると入手` : 'ドリルをノーミス取り切りで入手';
    }
    case 'drill-rank-cleared':
      return `${drillRankLabel(u.rank)} のドリルを ${u.minCount} 個クリアすると入手`;
    case 'drill-rank-no-miss':
      return `${drillRankLabel(u.rank)} の全ドリルをノーミスで取り切ると入手`;
    case 'admin-only':
      return '管理人特典';
    default:
      return '達成条件あり';
  }
};

/** インベントリパネル（🎒）のビューを最新の inventory ストアから構築して controls へ渡す。 */
const refreshInventoryPanel = (): void => {
  const state = inventory.current();
  const ownedSet = new Set(state.owned);
  const all = allItemDefinitions();
  // 全アイテムを「id / カテゴリ / 名前 / 説明 / 取得状況 / 装備状況 / 解錠条件テキスト / 難易度」で
  // 構築し UI 側へ渡す。ソート/フィルタはユーザー UI 操作に従って controls 側で行う。
  const items = all.map((def) => {
    const owned = ownedSet.has(def.id);
    const equipped = state.equipped[def.category] === def.id;
    return {
      id: def.id,
      category: def.category,
      name: def.name,
      description: def.description,
      owned,
      equipped,
      unlockText: formatUnlockHint(def),
      difficulty: unlockDifficulty(def),
    };
  });
  controlsApi.setInventoryView({
    items,
    isAdmin: inventory.isAdmin(),
    onEquip: (category, itemId) => {
      if (!serverStats || nickname.length === 0) return;
      // サーバー権威で装備変更。レスポンス（inventoryUpdate）で inventory.apply が呼ばれ、
      // subscribe → refreshInventoryPanel → applyCueShaftAppearance の順で UI 反映される。
      lobby.equipItem(nickname, category, itemId);
    },
  });
};
// 起動時に 1 度呼んで初期 invView を構築する。未ログイン（loopback・サーバー応答前等）でも
// inventory.subscribe コールバックが発火しない経路があるため、ここで明示的に push する。
// 内容は ITEM_REGISTRY の全件・owned/equipped はすべて false（サーバー状態は空＝既定）。
refreshInventoryPanel();

const stopMatchTimers = (): void => {
  if (matchHbTimer) {
    window.clearInterval(matchHbTimer);
    matchHbTimer = 0;
  }
  if (matchMonTimer) {
    window.clearInterval(matchMonTimer);
    matchMonTimer = 0;
  }
};

// 対戦/観戦を終了してソロへ戻す（切断時・退出時）。
const teardownMatch = (): void => {
  stopMatchTimers();
  if (transport) {
    transport.close();
    transport = null;
  }
  netConnected = false;
  // F-31 P2b §12.7: 観戦突入時に立てた Director 一時 override を解除（永続値へ復帰）。
  // override が立っていない場合は no-op（getBroadcastMode が再度コールバックを発火しないため安全）。
  setBroadcastModeSessionOverride(null);
  spectating = false;
  spectateMatchId = null;
  // P3.6 §14.16: 観戦終了でバー可視判定を再 sync（観戦時自動 ON が解除される）。
  syncBroadcastBarVisibility();
  netState = 'connected';
  localWantRematch = false;
  opponentWantRematch = false;
  recordedThisGame = false;
  // race 状態と CPU 戦フラグをクリア（永続化なし＝中断で破棄）。
  currentRace = null;
  raceBreakRunByMe = 0;
  cpuMatch = false;
  currentCpuPlayer = null; // 次の対戦に向けて CPU 識別子を解除
  shotByCpu = false; // CPU ショット中の teardown でも確実に解除
  if (cpuThinkTimer) {
    window.clearTimeout(cpuThinkTimer);
    cpuThinkTimer = 0;
  }
  if (raceContinueTimer) {
    window.clearTimeout(raceContinueTimer);
    raceContinueTimer = 0;
  }
  syncRaceScore();
  setMatchActive(false); // 設定パネルの自動照準ロックを解除（グレーアウト解除）
  // 軌跡ストリーミング状態をリセット。
  remotePlayActive = false;
  replayingRemote = false;
  trackPlayer = null;
  trackRecorder = null;
  ruleParams = { ...ruleParams, online: false };
  lobby.setPlaying(null); // idle に戻す
  controlsApi.setChatEnabled(false); // チャット送信UIを隠す
  setRackSpread(isBreakSpread()); // ばらつきをユーザー設定へ戻す
  resetRackAndAim('nineBall'); // ソロ盤面へ（online=false で再ラック）
  // バックグラウンド待機モードと通知済み Set もクリア（試合終了で再評価できるよう）。
  backgroundWaiting = false;
  controlsApi.setBackgroundWaitingMode(false);
  controlsApi.closeMatchInvitation();
  clearMatchInvitationNotified();
  syncRuleHud();
  syncLobbyView();
  // 中断（自発退出・相手退出・切断・観戦終了）後は次の行動を取りやすいよう
  // ロビーを自動オープン。既に開いていれば openLobby 側で no-op。
  controlsApi.openLobby();
  // Wake Lock を解放（ソロ復帰中はバッテリーを温存）。
  void wakeLock.release();
};

// ハートビート送信＋切断監視を開始する。
const startMatchTimers = (): void => {
  stopMatchTimers();
  lastPeerActivity = Date.now();
  netState = 'connected';
  matchHbTimer = window.setInterval((): void => {
    if (netConnected && !spectating) transport?.send({ t: 'ping' });
  }, 2000);
  matchMonTimer = window.setInterval((): void => {
    if (!netConnected) return;
    const elapsed = Date.now() - lastPeerActivity;
    if (elapsed >= DISCONNECT_AFTER_MS) {
      netState = 'disconnected';
      controlsApi.showMatchEvent(
        '相手切断',
        `⛔ ${opponentNickname || '相手'} と接続が切れました（無効試合）`,
        'abort',
      );
      teardownMatch();
    } else if (elapsed >= RECONNECT_AFTER_MS && netState === 'connected') {
      netState = 'reconnecting';
      syncRuleHud();
    }
  }, 1000);
};

// 対戦開始（ロビーのペア成立）。host=P0/ブレイカー。
// race 設定は host が決定し、guest 側は init メッセージで通知される（簡略実装: 第1ブレイカーは host）。
const startMatch = (
  room: string,
  role: 'host' | 'guest',
  raceConfig?: {
    readonly targetWins: RaceTargetWins;
    readonly breakRule: BreakRule;
    readonly opponentName: string;
  },
): void => {
  if (transport) transport.close();
  transport = createTransport(room);
  transport.onMessage(onNetMessage);
  myPlayer = role === 'host' ? 0 : 1;
  spectating = false;
  cpuMatch = false;
  netConnected = true;
  amWaiting = false;
  // 対戦成立でバックグラウンド待機モードと通知済み Set もクリア（対戦中は通知不要）。
  backgroundWaiting = false;
  controlsApi.setBackgroundWaitingMode(false);
  controlsApi.closeMatchInvitation();
  clearMatchInvitationNotified();
  localWantRematch = false;
  opponentWantRematch = false;
  recordedThisGame = false;
  playMode = 'nineBall';
  rulesActive = true;
  ruleParams = { ...ruleParams, online: true };
  // race 状態の初期化（race-to-N + ブレイク権ルール）。
  currentRace = initRaceState({
    targetWins: raceConfig?.targetWins ?? 1,
    breakRule: raceConfig?.breakRule ?? 'alternate',
    firstBreaker: 0, // host = P0 が第1ラックのブレイカー
    vsCpu: false,
    opponentName: raceConfig?.opponentName ?? opponentNickname ?? '相手',
  });
  raceBreakRunByMe = 0;
  setRackSpread(true); // 対戦中はブレイクのばらつき ON 固定
  setMatchActive(true); // 設定パネルの自動照準をロック表示（グレーアウト）
  lobby.setPlaying(room);
  controlsApi.setChatEnabled(true); // 対戦中はチャット送信を有効化
  controlsApi.closeLobby(); // 対戦確定でロビーを閉じる
  // 対戦開始バナー（中央・青系・2.6秒）。相手の実名と自分の役割（P1/P2）を明示。
  controlsApi.showMatchEvent(
    '対戦開始',
    `⚔ ${opponentNickname || '相手'}  ・  あなた = P${myPlayer + 1}  ・  先取${currentRace.targetWins}`,
    'start',
  );
  startMatchTimers();
  if (role === 'host') {
    resetRackAndAim('nineBall'); // ruleState も online で初期化
    transport.send({ t: 'init', state: ruleState, balls: serializeBalls(table) });
  }
  syncRuleHud();
  syncLobbyView();
  syncRaceScore();
  // 相手のショット待ち中に画面が消えないよう Wake Lock を取得（非対応環境では no-op）。
  void wakeLock.acquire();
};

// CPU 対戦の開始。transport は使わず、ルールエンジン＋CPU 思考でローカル完結。
// 成績/分析は集計しない（cpuMatch=true で recordResult/reportShot をスキップ）。
// 引数 cpu は対戦相手の CPU プレイヤー（id・displayName・style）。
const startCpuMatch = (
  targetWins: RaceTargetWins,
  breakRule: BreakRule,
  cpu: CpuPlayer,
): void => {
  // 既存のトランスポートがあれば閉じる（人間対戦からの切替対策）。
  if (transport) {
    transport.close();
    transport = null;
  }
  myPlayer = 0; // ユーザー = P0（ブレイカー固定）。CPU = P1。
  spectating = false;
  cpuMatch = true;
  currentCpuPlayer = cpu; // 思考スタイル・UI 表示名を駆動するため保持
  netConnected = true; // 対戦中扱い（HUD/入力ロック等の既存ロジック流用）
  amWaiting = false;
  // CPU 戦開始でバックグラウンド待機モードと通知済み Set をクリア（次の遷移で再評価）。
  backgroundWaiting = false;
  controlsApi.setBackgroundWaitingMode(false);
  controlsApi.closeMatchInvitation();
  clearMatchInvitationNotified();
  localWantRematch = false;
  opponentWantRematch = false;
  recordedThisGame = false;
  playMode = 'nineBall';
  rulesActive = true;
  // ruleParams.online=true でルールが2P判定（誤球ファウル等）になる。
  ruleParams = { ...ruleParams, online: true };
  opponentNickname = cpu.displayName;
  // 第1ラックのブレイカーはコイントスで決定（将来的にはバンキングで決定する仕様に置換予定）。
  // race-to-N の2戦目以降は currentRace の breakRule（'alternate' / 'winner'）に従う。
  const firstBreaker: 0 | 1 = Math.random() < 0.5 ? 0 : 1;
  currentRace = initRaceState({
    targetWins,
    breakRule,
    firstBreaker,
    vsCpu: true,
    opponentName: cpu.displayName,
  });
  raceBreakRunByMe = 0;
  setRackSpread(true);
  setMatchActive(true);
  lobby.setPlaying(`cpu-${Date.now()}`);
  // CPU 戦はチャット無効・切断監視不要（startMatchTimers は呼ばない）。
  controlsApi.setChatEnabled(false);
  controlsApi.closeLobby();
  controlsApi.showMatchEvent(
    `${cpu.displayName} 戦`,
    `🎱 先取${targetWins} ・ ブレイク権=${breakRule === 'alternate' ? '交互' : '勝者'} ・ 先攻=${firstBreaker === 0 ? 'あなた' : cpu.displayName}`,
    'start',
  );
  // ⚠ 重要: resetRackAndAim は ruleState.breaker を「前回値の引き継ぎ」で初期化するため、
  // 先に ruleState を firstBreaker で書き換えておかないと、Math.random() で決めたブレーク権が
  // 反映されず「前回と同じ人がブレーク」になる（バグ事例: 2026-06-12 ユーザー報告）。
  ruleState = initRuleState({ breaker: firstBreaker });
  resetRackAndAim('nineBall');
  syncRuleHud();
  syncLobbyView();
  syncRaceScore();
  // CPU 戦も思考演出時間（0.9〜1.5 秒）が積み重なるため Wake Lock を取得。
  void wakeLock.acquire();
};

// 観戦開始（読み取り専用購読・入力全ロック）。
const startSpectate = (matchId: string): void => {
  if (transport) transport.close();
  transport = createTransport(matchId);
  transport.onMessage(onNetMessage);
  spectating = true;
  spectateMatchId = matchId;
  netConnected = true;
  amWaiting = false;
  playMode = 'nineBall';
  rulesActive = true;
  ruleParams = { ...ruleParams, online: true };
  setMatchActive(true); // 観戦中も設定パネルの自動照準をロック表示
  lobby.setSpectating(matchId);
  controlsApi.setChatEnabled(true); // 観戦者もチャット送信可（名前付きで表示）
  controlsApi.closeLobby();
  startMatchTimers();
  syncRuleHud();
  syncLobbyView();
  // F-31 P2b §12.7: 観戦突入時、保存値が OFF かつ「観戦中は Director 既定 ON」が有効なら
  // 一時的に Director を適用（永続化しない）。manual/director 保存ユーザーは保存値尊重。
  // 観戦終了の teardownMatch で session override を null に戻す。
  if (getBroadcastMode() === 'off' && isBroadcastSpectatorDefault()) {
    setBroadcastModeSessionOverride('director');
  }
  // P3.6 §14.16: 観戦中は本設定に関わらずバーを強制表示する判定のため、ここで再 sync。
  syncBroadcastBarVisibility();
  // 観戦中もショット待ちで画面が消えないよう Wake Lock を取得。
  void wakeLock.acquire();
};

// 対戦勧誘ダイアログを表示すべきか・どの相手かを返す（条件を満たさない場合 null）。
// 条件:
//  - 既に勧誘ダイアログが開いている／race モーダル／opponent picker（非バックグラウンド）が開いている → 抑止
//  - 対戦中（netConnected ∧ !spectating）または観戦中 → 抑止（対戦中の通知は意味なし）
//  - cpuMatch 中: 物理静止 ∧ CPU 思考スケジュール無し（人の手番中の静止のみ）
//  - backgroundWaiting=true でも cpuMatch=false ならいつでも OK（ソロプレイ中）
//  - roster 内に「未通知 ∧ waiting ∧ 自分以外 ∧ 人間」が居れば最初の 1 件を返す
const pickMatchInvitationCandidate = (): { userId: string; nickname: string } | null => {
  if (controlsApi.isMatchInvitationOpen()) return null;
  // 対戦中／観戦中は通知しない（cpuMatch 中は通知対象＝ここでは spectate と人間対戦のみ弾く）。
  if (spectating) return null;
  if (netConnected && !cpuMatch) return null;
  // バックグラウンド待機モード or CPU 戦中でのみ通知。それ以外（通常ソロ・picker 開放中）は通知しない。
  if (!backgroundWaiting && !cpuMatch) return null;
  // CPU 戦中は静止＋思考タイマー無し時のみ通知（操作の邪魔をしない）。
  if (cpuMatch) {
    if (!isResting(table)) return null;
    if (cpuThinkTimer !== 0) return null;
  }
  for (const u of lastRoster) {
    if (u.status !== 'waiting') continue;
    if (u.userId === lobby.userId) continue;
    if (isCpuUserId(u.userId)) continue;
    if (notifiedOpponentIds.has(u.userId)) continue;
    return { userId: u.userId, nickname: u.nickname };
  }
  return null;
};

// roster から該当 userId が消えたら通知済み Set から削除（再オンライン時に再通知可能にする）。
const pruneNotifiedSetByRoster = (roster: Roster): void => {
  if (notifiedOpponentIds.size === 0) return;
  const aliveIds = new Set(roster.map((u) => u.userId));
  for (const id of Array.from(notifiedOpponentIds)) {
    if (!aliveIds.has(id)) notifiedOpponentIds.delete(id);
  }
};

const maybeShowMatchInvitation = (): void => {
  const target = pickMatchInvitationCandidate();
  if (!target) return;
  controlsApi.showMatchInvitation({
    opponentUserId: target.userId,
    opponentNickname: target.nickname,
  });
};

// ロビー配線（在席更新・ペア成立・初回ニックネーム）。
lobby.onRoster((roster: Roster) => {
  lastRoster = roster;
  syncLobbyView();
  // 在席ユーザーの inventory snapshot をキャッシュ（presence 同梱の stats から取得可）。
  // 手番別キュー表示で「対戦相手の装備」を解決するために使う。
  let opponentInvUpdated = false;
  for (const u of roster) {
    const snap = u.stats?.inventory;
    if (snap) {
      knownInventoriesByName.set(u.nickname, snap);
      if (u.nickname === opponentNickname) opponentInvUpdated = true;
    }
  }
  if (opponentInvUpdated) applyActiveCueAppearance();
  // 観戦中はロスター更新（参加者の実名取得・改名）を HUD の手番表示へ反映する。
  if (spectating) syncRuleHud();
  // §7A: 相手選択モーダルが開いていれば候補を更新（waiting 中の他者・CPU）
  if (amWaiting && !backgroundWaiting) {
    controlsApi.updateOpponentPickerCandidates(buildOpponentCandidates());
  }
  // 通知済み Set: roster から消えたユーザーは削除（再オンライン時に再通知可能）
  pruneNotifiedSetByRoster(roster);
  // バックグラウンド待機モード or CPU 戦中の新規 waiting 対人 → 勧誘ダイアログ
  maybeShowMatchInvitation();
});
// （旧）自動マッチの成立通知。§7A 指名マッチ移行（2026-06-09）に伴い廃止。
// 万一旧フロー経由で呼ばれても何もしない（loopback-lobby の tryPair も no-op 化済み）。
lobby.onMatched((_m: MatchAssignment) => {
  // no-op: 指名マッチは onPickAccepted → onRaceStart 経由で対戦開始する
});

// §7A 指名マッチ成立通知（pickAccepted）。
lobby.onPickAccepted((p: RaceProposal) => {
  // 申込先が CPU の場合: ラック設定モーダルを vsCpu=true で開く
  // （p.matchId は 'cpu-...' プレフィックス。CPU の userId は p.opponent.userId）
  const isCpu = isCpuUserId(p.opponent.userId);
  if (isCpu) {
    const cpu = getCpuPlayer(p.opponent.userId);
    pendingRaceStart = { kind: 'cpu' };
    pendingCpuPlayer = cpu;
    controlsApi.closeOpponentPicker();
    controlsApi.showRaceModal({
      opponentName: cpu?.displayName ?? p.opponent.nickname,
      vsCpu: true,
    });
    return;
  }
  // 人間対戦
  opponentNickname = p.opponent.nickname;
  controlsApi.closeOpponentPicker();
  controlsApi.showRaceModal({
    opponentName: p.opponent.nickname,
    vsCpu: false,
    matchId: p.matchId,
    initialRace: p.raceA,
  });
});

// §7A 申込失敗（busy 等）。
lobby.onPickFailed((reason: PickFailReason) => {
  if (reason === 'busy') {
    controlsApi.showToast('相手は他の対戦に入りました。別の相手を選んでください。');
  }
  // OpponentPicker は開いたまま（候補を更新して継続）。
  if (amWaiting) {
    controlsApi.updateOpponentPickerCandidates(buildOpponentCandidates());
  }
});

// §7B ラック設定のリアルタイム双方向通知。
lobby.onRaceUpdated((u: RaceUpdated) => {
  // 自分の echo は無視（モーダル UI を 2 重更新しない）
  if (u.byUserId === lobby.userId) return;
  controlsApi.updateRaceModalRemote(u.race, u.accepted);
});

// §7B 両者承諾で対戦開始。
lobby.onRaceStart((s: RaceStartInfo) => {
  controlsApi.closeRaceModal();
  controlsApi.closeOpponentPicker();
  // host/guest 判定
  const role: 'host' | 'guest' = s.hostId === lobby.userId ? 'host' : 'guest';
  opponentNickname = s.opponent.nickname;
  startMatch(s.matchId, role, {
    targetWins: s.race.targetWins,
    breakRule: s.race.breakRule,
    opponentName: s.opponent.nickname,
  });
});

// §7B ラック設定モーダル内チャット受信。
// broadcast 経路のため自分の送信 echo も届きうる（loopback の BroadcastChannel は自エコーしないが、
// PartyKit 経由でも default 経路の `[sender.id]` 除外で自エコーは届かない契約）。念のため自 echo を弾く。
lobby.onLobbyChat(
  (
    _matchId: string,
    byUserId: string,
    byNickname: string,
    text: string,
    voice?: { readonly pitch: number; readonly rate: number; readonly gender: 'female' | 'male' },
  ) => {
    if (byUserId === lobby.userId) return; // 自分の送信 echo は無視
    // matchId 不一致のメッセージ（別ラック設定セッションのもの）が届くケースは現状ない
    // （クライアントは現在開いているモーダルにのみ書き込み・関係ない matchId は controls 側で吸収）。
    // voice が同梱されていれば「相手の設定に従う」読み上げを行う（無ければ既定）。
    controlsApi.showLobbyChatInRaceModal(text, 'opponent', byNickname, voice);
  },
);

// §7B ラック設定中断。
lobby.onRaceAborted((a: RaceAborted) => {
  controlsApi.closeRaceModal();
  // 理由に応じてトースト
  if (a.reason === 'cancelled') {
    controlsApi.showToast('相手が対戦設定をキャンセルしました');
  } else if (a.reason === 'disconnected') {
    controlsApi.showToast('相手が離脱しました');
  }
  // 相手選択モーダルへ戻る（waiting 維持）
  if (amWaiting) {
    controlsApi.showOpponentPicker({ candidates: buildOpponentCandidates() });
  }
});
// サーバー成績の更新通知（試合終了時に全クライアントへ broadcast）。
lobby.onStats((name, stats) => {
  // 順位表キャッシュを差分更新（該当行のみ・全件再取得は不要）。lastSeen は今に更新。
  const next: LeaderboardEntry = {
    name,
    wins: stats.wins,
    losses: stats.losses,
    lastSeen: Date.now(),
  };
  const rest = leaderboard.filter((e) => e.name !== name);
  leaderboard = [...rest, next];
  // 自分の名前なら通算成績表示も同期（端末間共有）。
  if (name === nickname) {
    matchStats.wins = stats.wins;
    matchStats.losses = stats.losses;
  }
  // 任意のユーザーの inventory snapshot をキャッシュ（後で「相手手番にそのユーザーの
  // キューを表示する」ために使う）。snapshot は stats に同梱されており、対戦相手・
  // 順位表上位ユーザー等の装備が逐次キャッシュされる。
  if (stats.inventory) {
    knownInventoriesByName.set(name, stats.inventory);
    // 現在の対戦相手の inventory が更新された場合は即時反映（装備変更にライブ追従）。
    if (name === opponentNickname) applyActiveCueAppearance();
  }
  syncLobbyView();
});
// 改名で旧名が消えたら順位表キャッシュから除去。
lobby.onStatsRemove((name) => {
  leaderboard = leaderboard.filter((e) => e.name !== name);
  syncLobbyView();
});

// インベントリ更新（取得・装備変更）通知。自分宛のみ反映する（権威はサーバー）。
lobby.onInventoryUpdate((u) => {
  if (u.name !== nickname) return; // 他人の inventory は通常届かないが防御的に
  inventory.apply({
    owned: u.inventory.owned,
    equipped: Object.fromEntries(
      Object.entries(u.inventory.equipped).filter(([, v]) => typeof v === 'string'),
    ) as Readonly<Partial<Record<'cue' | 'cloth' | 'badge', string>>>,
  });
  if (u.grantedIds.length > 0) {
    // 新規取得アイテムごとに🎁バナーを表示（複数同時取得もあり得る）。
    for (const id of u.grantedIds) {
      const def = getItemDefinition(id);
      const label = def?.name ?? id;
      controlsApi.showMatchEvent('🎁 アイテム獲得!', label, 'win');
    }
  }
});
// 起動時の認証フロー（保存名で自動ログイン or 名前入力 → 新規登録/PIN）。controls がモーダルを駆動。
controlsApi.startAuthFlow(nickname);
syncLobbyView();

// 起動時の初期化: 最若球へ照準＋カメラを構図化する（初期照準を的玉へ向ける）。
aimAtLowestBall();
rig.reset(cue.aim.angle + Math.PI);
// 狙撃カム残留対策（保険）: rig.reset() でも shotFrozen=false へ戻るが、念のため releaseFreeze(0)
// で freezeReleaseBlend=0 まで完全クリアする（レジューム経路でなんらかの状態異常が残っても確実
// にゼロから始める）。狙撃カム用のローカル状態（shotFreezeCamActive 等）はモジュールトップで
// false 初期化済みのためここでは触らない（宣言が下方にあるため TDZ 回避）。
rig.releaseFreeze(0);
if (resumed && savedSession) {
  // レジューム: 再ラックせず前回の配置状態・進行中ランを復旧する。
  breakRun.resumeRun(savedSession.runAlive);
  if (savedSession.broken) {
    controlsApi.enterResumed({
      broken: true,
      ballInHand: savedSession.ballInHand,
    });
  } else {
    // ブレーク前で中断していた場合は通常の配置モードで再開する。
    controlsApi.enterPlacement();
  }
} else {
  // 新規開始: ブレーク配置モードに入り、初期ラックもブレイク開始として記録対象にする。
  controlsApi.enterPlacement();
  breakRun.startRack();
  persist(); // 初期盤面を保存（以後バックグラウンド化しても復元できる）。
}

// ルール状態の復元: 永続された RuleState（手番/フェーズ/連続ファウル/勝敗）を
// そのまま復旧する。loadSession 側で構造妥当性は検証済み。
// rule が無い場合（旧版データ等）のみ、従来の概算（broken→inPlay）にフォールバックする。
if (resumed && savedSession && rulesActive) {
  if (savedSession.rule) {
    ruleState = savedSession.rule;
  } else {
    ruleState = savedSession.broken
      ? { ...initRuleState({ breaker: 0 }), phase: 'inPlay' }
      : initRuleState({ breaker: 0 });
  }
}
syncRuleHud(); // 起動時に HUD を初期表示（非ルールモードでは隠す）。

const clock = new THREE.Clock();
const FIXED_DT = 1 / 240;
let accumulator = 0;

// キュー mesh とカメラの近接判定用に再利用するベクトル（毎フレーム new しない）。
const cueMidTmp = new THREE.Vector3();

// 再生フェーズ管理: ショット中（発射〜全静止）か。エッジの取りこぼしを防ぐため
// 「フレーム開始時に動いていれば発射」「全静止したら終了」を状態で管理する
// （弱いショットが1フレームの while 内で発射〜静止まで完結しても確実に検出する）。
let shotActive = false;
// フォローカメラの「的球」= 撞いた瞬間に捕捉した照準先の球（なければ最若球）。
let followTargetId: number | null = null;
// 球速HUD: このショットがブレイクか（onShoot で確定）と、その瞬間最高速度 [m/s]。
// ブレイクおよびプラクティスは全ショットで表示し、ショット中の最高速度を静止するまで保持表示する。
let shotIsBreak = false;
let breakPeakSpeed = 0;

// キュー仰角を次の構え（aimReady の立ち上がり）でリセットするための予約フラグと前フレーム値。
// ショット静止直後にユーザー操作仰角を保持していると次のショットへ持ち越されてしまう問題への対処。
// ショット完了時に armed=true、次に aimReady が false→true へ遷移した瞬間に resetCueElevation を呼ぶ。
let elevationResetArmed = false;
let prevAimReady = false;

// STOPショット相当の引き量を通過した瞬間に1回だけキューをきらめかせるためのエッジ検出と
// デバウンス。1回の構え（パワー>0 連続区間）あたり最大1回。パワーが0に戻れば再アーム。
let stopShotPrevIsStop = false;
let stopShotGlimmerArmed = true;
let shotElapsed = 0; // ショット開始からの経過秒（安全網タイマー）
let nearRestElapsed = 0; // 全球が極低速で経過した秒（近接静止判定）
// 狙撃カム（ショットフリーズ）: このショットでカメラフリーズを発動しているか。
//   - active: 発動中（rig 側 isShotFrozen() と独立に維持。release 後も「このショットの監視は終了」フラグとして使う）
//   - targetBallId: 対象的玉 ID（vel/pos を毎フレーム監視）
//   - pocketPos: 狙うポケット中心（明らかに外れ判定の基準）
//   - released: 既に releaseFreeze を呼んだか（多重 release 防止）
let shotFreezeCamActive = false;
let shotFreezeTargetBallId: number | null = null;
let shotFreezePocketPos: { x: number; y: number } | null = null;
let shotFreezeReleased = false;
// 「明らかに外れ」判定は的玉が**最初に動き出したフレーム**でのみ評価する。
// 後段で他球やクッションに触れて方向が変わっても、最初のヒット方向だけで判断するため、
// カロム/コンビ経由でポケットに入るケースを誤って早期解除しない。
let shotFreezeMissChecked = false;
// 狙撃カム ポケットイン時の「視覚的沈み込み待ち」遅延（秒）。pot イベントは球がポケット
// 淵を越えた瞬間に発火するが、視覚的にはまだ球が沈み込み中。即時解放だとカメラが視覚に
// 追いつく前に動き出して違和感が出るため、この遅延後に releaseFreeze を呼ぶ。
// pot 以外の解放トリガー（cushion / clearly-missing / target-off-table）は球がポケットに
// 向かう瞬間ではないので遅延不要（従来どおり即時解放）。
let shotFreezePotReleaseDelay = 0;
const SHOT_FREEZE_POT_RELEASE_DELAY = 0.3;
const SHOT_FREEZE_POT_RELEASE_EASE = 0.8;
// 狙撃カム解放後の「方向だけ」追従における方位の自前 slow lerp 値。
// 既定の alignToAim 内部 lerp は時定数 0.001（≈150°/秒）で初期回転が速すぎる体感。
// main.ts 側で時定数 0.1（≈1秒で90%収束）の slow lerp を当て、自然な追従にする。
// null は「未初期化」＝最初のフレームで現在 azimuth から開始する。
let postFreezeAzimuth: number | null = null;
const POST_FREEZE_AZIMUTH_LERP_BASE = 0.1;
// 角度 a → b への wrap-aware lerp（[-π, π] へ正規化した差分を t 倍）。
const lerpAngle = (a: number, b: number, t: number): number => {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};
// ブレイクラン判定用: ショット間（静止中）の安定した的玉数と、発射時点で捕捉した値。
// 発射検出フレームでは既に物理が進んでいるため、静止中に保持した値を基準にする。
let objBallsAtRest = objectBallsRemaining(table);
let objBallsAtFire = objBallsAtRest;
const SETTLE_TIMEOUT = 25; // これを超えて静止しなければ強制静止（最終保険）
const REST_SPEED_EPS = 0.02; // 「ほぼ停止」とみなす最大水平速度 [m/s]
const REST_HOLD = 0.4; // 極低速がこの秒数続いたら強制静止（数値リミットサイクル対策）

const animate = (): void => {
  requestAnimationFrame(animate);
  const frame = Math.min(clock.getDelta(), 0.05);
  accumulator += frame;
  wallMs += frame * 1000; // 再生タイミング用の単調増加クロック

  // CPU 戦: CPU の手番が回ってきたら思考→発射をスケジュールする（自身でデバウンス）。
  maybeTriggerCpuTurn();

  // ステップ前の静止状態（このフレーム開始時に動いているか）。
  const restingBefore = isResting(table);

  while (accumulator >= FIXED_DT) {
    // 遠隔再生中（remotePlayActive）は物理を進めない（軌跡の補間再生に委ねる）。
    if (!remotePlayActive && !isResting(table)) {
      // 衝突解決が破綻（Depth exceeded 等）してもアプリ全体を止めず、強制静止で復帰する。
      try {
        step(table, FIXED_DT);
      } catch (err) {
        console.error('物理ステップで例外。強制静止して継続します:', err);
        forceSettle(table);
      }
      // 送信側（権威・遠隔）: 物理ステップごとに球位置をサンプリングして後でライブ送信。
      if (trackRecorder) {
        shotPlayMs += FIXED_DT * 1000;
        trackRecorder.sample(shotPlayMs, onTablePositions(table));
      }
    }
    accumulator -= FIXED_DT;
  }

  // 送信側: このフレームで貯まった軌跡サンプルを相手へ送る（ライブ・バッチ）。
  if (trackRecorder) {
    const samples = trackRecorder.drain();
    if (samples.length > 0) transport?.send({ t: 'track', s: samples });
  }

  // 受信側（遠隔）: 軌跡を補間再生して球位置を駆動（物理は進めない）。
  if (remotePlayActive && trackPlayer) {
    const playMs = wallMs - remotePlayStartMs - TRACK_BUFFER_MS;
    if (playMs > 0 && trackPlayer.hasData()) {
      const f = trackPlayer.frameAt(playMs);
      if (f.size > 0) applyPlaybackFrame(table, f, frame);
      // ショット中にポケットインした球（トラックが入口で途切れた球）は、入口で停止
      // させず確実に落球させる（frameAt から除外済み＝位置駆動されないので二重に動かない）。
      const potted = trackPlayer.pottedIds(playMs);
      if (potted.length > 0) forcePotBalls(table, new Set(potted));
    }
    // 再生終了: 最後のサンプルを過ぎ、かつ権威確定（shotEnd）が来ていれば適用して終了。
    if (pendingAuthoritative && playMs >= trackPlayer.lastMs()) {
      applyAuthoritative(pendingAuthoritative.state, pendingAuthoritative.balls);
      remotePlayActive = false;
      replayingRemote = false;
      trackPlayer = null;
      rig.setOverview(false);
      rig.setAutoTopView(false);
    }
  }

  // 物理イベント（球衝突/クッション/ポケット・id 付き）→効果音＋ルール観測。
  // 常に drain して engine の outcome ログ肥大を防ぐ。再生は設定ON時のみ。
  const outcomes = drainShotOutcomes(table);
  // ルール判定／ドリル合否判定用にショット中のイベントを蓄積（打前に preShot を立てている間のみ）。
  // ★ドリル（rulesActive=false）でも蓄積しないと endShotObservation の firstContactId が
  //   常に null になり、judgeDrillShot の targetOrder 判定で毎回 miss＝クリア不能になる（F-30 不具合）。
  if (preShot && (rulesActive || currentDrill)) {
    for (const o of outcomes) shotOutcomes.push(o);
    // ブレイク中: ヘッドストリングを越えた的玉を記録（越えて戻った球も和集合で保持）。ルール時のみ。
    if (rulesActive && ruleState.phase === 'breakPlacement') {
      for (const id of objectBallsBehindHeadString(table)) breakHeadStringIds.add(id);
    }
  }
  if (isSoundEnabled()) {
    for (const o of outcomes) {
      // クッション反射音は廃止（cushion は鳴らさない）。
      if (o.type === 'collision') sound.collision(o.speed);
      else if (o.type === 'pot') sound.pocket(o.speed);
    }
  }

  const resting = isResting(table);
  const cb = table.cueball.pos;

  // F-31 Director: 毎フレーム tick を発火して内部時計を進める（プリセット遷移はイベント駆動）。
  dispatchDirector({ type: 'tick', nowMs: wallMs });

  // F-31 Director: ユーザー手動操作検知（false → true の遷移時のみ発火）。
  const isOverriddenNow = rig.isUserOverridden();
  if (isOverriddenNow && !lastUserOverrideObserved) {
    dispatchDirector({ type: 'user-override-detected' });
  }
  lastUserOverrideObserved = isOverriddenNow;

  // F-31 Director: ルールフェーズ遷移検知（break/normal/win の切り替わり時のみ発火）。
  const phaseNow = resolveDirectorPhase();
  if (phaseNow !== lastRulePhaseObserved) {
    dispatchDirector({ type: 'phase-changed', phase: phaseNow });
    lastRulePhaseObserved = phaseNow;
  }

  // F-31 P3.4 §14.14 first-contact 検出: 手玉と他球の最初の ball-ball collision で
  // POCK へ切替（cushion は対象外＝kick shot 等で先にレールに当たる場合は無視）。
  // shotActive 中のみ・Director モードのみ・1 ショット 1 回。
  if (
    shotActive &&
    !firstContactDispatchedThisShot &&
    getBroadcastMode() === 'director'
  ) {
    for (const o of outcomes) {
      if (o.type === 'collision' && (o.a === 0 || o.b === 0)) {
        // 手玉が関与する最初の ball-ball collision
        dispatchDirector({ type: 'first-contact' });
        firstContactDispatchedThisShot = true;
        break;
      }
    }
  }

  // F-31 Director: outcomes の pot を ball-pot として通知（9 番なら勝利球フラグ）。
  for (const o of outcomes) {
    if (o.type === 'pot') {
      const isWin = getPlayMode() === 'nineBall' && o.a === 9;
      dispatchDirector({ type: 'ball-pot', ballId: o.a, isWinBall: isWin });
      // F-31 P2b §12.6: 9 番勝利シーケンサ起点。Director の ball-pot 内で POCK に
      // 切り替わるため、ここでは時系列スケジューラだけ起動する（重複起動防止のため
      // winSequenceStartWallMs が null のときのみセット）。
      if (
        isWin &&
        getBroadcastMode() === 'director' &&
        winSequenceStartWallMs === null
      ) {
        winSequenceStartWallMs = wallMs;
        winSeqSlowApplied = false;
        winSeqTopHeadDispatched = false;
      }
    }
  }

  // F-31 P2b §12.6 勝利シーケンス進行: ball-pot 検知時刻からの相対時間で 3 段に分けて作用。
  // - T+0.3s: rig.setSlowMotion(0.5) でカメラ補間半速化（物理 dt は不変＝決定論維持）
  // - T+0.8s: dispatchDirector('win-sequence-tophead') で POCK → Hot Head へ smoothCutTo
  // - T+3.0s: rig.setSlowMotion(1) で通常速度復帰・シーケンス終了
  if (winSequenceStartWallMs !== null) {
    const elapsed = wallMs - winSequenceStartWallMs;
    if (elapsed >= WIN_SEQ_SLOW_AT_MS && !winSeqSlowApplied) {
      rig.setSlowMotion(WIN_SEQ_SLOWMO_SCALE);
      winSeqSlowApplied = true;
    }
    if (elapsed >= WIN_SEQ_TOPHEAD_AT_MS && !winSeqTopHeadDispatched) {
      dispatchDirector({ type: 'win-sequence-tophead' });
      winSeqTopHeadDispatched = true;
    }
    if (elapsed >= WIN_SEQ_END_AT_MS) {
      rig.setSlowMotion(1.0);
      winSequenceStartWallMs = null;
      winSeqSlowApplied = false;
      winSeqTopHeadDispatched = false;
    }
  }

  // F-31 P3 §14: shot-fired-hard 検知は P2b の経路だったが、ショット中カット禁止帯
  // （§13 F-2 / §14.2.4）に違反するため P3 で廃止。LOW プリセットは manual 専用に縮退。
  // 球速ピーク追跡フラグも除去。

  // F-31 P2b §12.3 Framing Safety — Director モード時、ショット中に主要 3 点（手玉・的玉・
  // 狙うポケット）が連続して FRAMING_LOST_TIMEOUT_MS 以上画面外なら framing-lost を dispatch。
  if (
    shotActive &&
    !framingLostDispatchedThisShot &&
    getBroadcastMode() === 'director'
  ) {
    const sc = buildBroadcastSceneContext();
    const points: ReadonlyArray<{ x: number; y: number }> = [
      sc.cueBall,
      sc.targetBall,
      sc.aimedPocket,
    ].filter((p): p is { x: number; y: number } => p !== null);
    if (points.length > 0) {
      const inFrame = pointsInFrustum(ctx.camera, points, FRAMING_MARGIN_M);
      if (!inFrame) {
        if (framingLostStartWallMs === null) framingLostStartWallMs = wallMs;
        else if (wallMs - framingLostStartWallMs > FRAMING_LOST_TIMEOUT_MS) {
          dispatchDirector({ type: 'framing-lost' });
          framingLostDispatchedThisShot = true;
          framingLostStartWallMs = null;
        }
      } else {
        framingLostStartWallMs = null; // 再び画面内に戻ったらリセット
      }
    }
  }

  // F-31 P3 §14.2.4 idle-too-long 検知（Broadcast Master 30 秒以上で WIDE へ）。
  // - Director モードのみ・ショット非実行中のみ（ショット中の派手な切替は避ける）。
  // - 1 つの "stay" につき 1 回（current が変わると再武装）。
  if (
    !shotActive &&
    !remotePlayActive &&
    getBroadcastMode() === 'director' &&
    directorState.current === 'broadcast-master' &&
    idleTooLongDispatchedFor !== `${directorState.lastSwitchAtMs}`
  ) {
    if (wallMs - directorState.lastSwitchAtMs > IDLE_TOO_LONG_MS) {
      dispatchDirector({ type: 'idle-too-long' });
      idleTooLongDispatchedFor = `${directorState.lastSwitchAtMs}`;
    }
  }
  // current が変わった（jib-sweep など別プリセットへ遷移）ら idleTooLong フラグを再武装する。
  if (directorState.current !== 'broadcast-master') idleTooLongDispatchedFor = null;

  // F-31 P3 §14: per-frame の pocket-approach 検知は廃止（§13 F-2 違反＝ショット中カット）。
  // ポケットカム選定はショット発射の直前に prepare-pocket-cam で 1 回だけ行う設計へ移行。
  // 配線は下記の「ショット発射」ブロック内（`prepare-pocket-cam` dispatch）参照。

  // ショット発射: 静止していない状態に入った瞬間（フレーム開始時に動いていれば発射）。
  // 狙う前に視点を手動回転していると userOverride が立って無視されるため、発射時にクリア。
  if (!remotePlayActive && !shotActive && !restingBefore) {
    shotActive = true;
    shotElapsed = 0;
    nearRestElapsed = 0;
    // ショット音は controls.onShoot（打撃の瞬間）で発音済み（フレーム遅延回避）。
    // ブレイクラン判定: このショット前（静止中）の的玉数を基準として捕捉。
    objBallsAtFire = objBallsAtRest;
    breakPeakSpeed = 0; // 新ショット: 球速ピークをリセット
    rig.clearUserOverride();

    // 狙撃カム（shot freeze cam）判定:
    //   設定 ON ＋ ローカル人間ショット（CPU/リモート再生は対象外）＋
    //   ターゲット球の特定可＋手玉・的玉・狙うポケットが画面内 → 発動。
    //   発動時は通常の autoTop/follow を抑止して構図を固定する。
    //   ブレイクショットは除外: 開幕でラックが散らばる構図ではターゲット1球の周辺だけを
    //   ズームしても散球を見失い違和感が出るため（ユーザー要望 2026-06-16）。
    //   判定: ルール時は phase==='breakPlacement'。プラクティス/ドリルはブレイク概念が
    //   薄いので一律で発動可能とする（ドリルは球数が散らない＋ハイズームの没入が有益）。
    shotFreezeCamActive = false;
    shotFreezeTargetBallId = null;
    shotFreezePocketPos = null;
    shotFreezeReleased = false;
    shotFreezeMissChecked = false;
    // 解放遅延カウントダウン・方位 slow lerp を新ショットへ持ち越さない。
    shotFreezePotReleaseDelay = 0;
    postFreezeAzimuth = null;
    // 二重保険: 前ショットで何らかの理由（リモート再生中の経路・モード切替・例外）で
    // rig 側のフリーズが残っていた場合に備え、新ショット発射前に必ず即時解除する。
    // freezeForShot を呼ぶ場合はその直後に shotFrozen=true で上書きされるため副作用なし。
    rig.releaseFreeze(0);
    const isBreakShotNow = rulesActive && ruleState.phase === 'breakPlacement';
    // F-31 Broadcast ON 時は Director（P2）または手動プリセット（P1）がカメラ制御を担うため、
    // 既存の F-03 狙撃カム経路を suppress する（設計書 broadcast-camera-mode.md §1.3 統合実装）。
    if (
      isShotFreezeCam() &&
      !isBroadcastOn() &&
      !shotByCpu &&
      !spectating &&
      !isBreakShotNow
    ) {
      // ターゲット球 id は controlsApi 経由（aimTargetId）。null の時はフォールバックとして
      // lowestOnTableBallId（最若球）を採用する。ルール時の状態遷移で aimTargetId が
      // 一瞬 null になるケース（applyRuleResult 内の resetRackAndAim 経由）でも発動を維持する。
      const ctrlTargetId = controlsApi.getAimTargetId?.() ?? null;
      const targetId = ctrlTargetId !== null ? ctrlTargetId : lowestOnTableBallId(table);
      const cueBall = table.cueball;
      if (targetId !== null && cueBall.onTable()) {
        const targetBall = table.balls.find((b) => b.id === targetId && b.onTable());
        if (targetBall) {
          const cuePos = { x: cueBall.pos.x, y: cueBall.pos.y };
          const targetPos = { x: targetBall.pos.x, y: targetBall.pos.y };
          const pockets = getPocketAims().map((p) => ({ x: p.x, y: p.y }));
          const resolved = fallbackTargetPocket(cuePos, targetPos, pockets);
          if (resolved) {
            // 視錐台判定にマージン（球4個分≈0.13m）を持たせ、ポケットが画面端ギリギリでも
            // 「球の一部が画面内」なら発動する（拡大ビュー時の境界判定 fail を救済）。
            const FRAME_MARGIN_M = 4 * ballRadius;
            const inFrame = pointsInFrustum(
              ctx.camera,
              [cuePos, targetPos, resolved.pos],
              FRAME_MARGIN_M,
            );
            if (inFrame) {
              // 発動: 通常の自動演出（autoTop/follow）を抑止しつつカメラを凍結する。
              shotFreezeCamActive = true;
              shotFreezeTargetBallId = targetId;
              shotFreezePocketPos = resolved.pos;
              rig.freezeForShot();
            }
          }
        }
      }
    }
    // フリーズ非発動時のみ従来の自動演出を有効化する。
    // Broadcast 'director' モード時は Director がカメラ管理を担うため自動演出を抑止。
    if (!shotFreezeCamActive && getBroadcastMode() !== 'director') {
      if (useAutoTopForShot()) rig.setAutoTopView(true);
      else rig.setFollow(true); // 非トップビュー: ダイナミック・フォローフレーミング
    }
    // F-31 P3 §14.4: shot-fired 直前に「狙うポケット」を事前選定し Director state へ流す。
    // ブレイクショット時は対象球が無いため事前選定なし（ball-pot が来てもフォールバック動作）。
    //
    // P3.3 改修: fallbackTargetPocket が null（カット/バンク/キック/セーフティ等で前方扇形
    // 内にポケットが無い場合）でも、**target ball の最近接ポケット**を fallback として採用。
    // これで「対象ポケットがビューに入らない多発」を解消する。厳密に正しくないこともあるが、
    // ショット中 POCK が確立できないままより、ある程度近いポケットを表示する方が UX 上有益。
    if (getBroadcastMode() === 'director' && !isBreakShotNow) {
      const cuePos = { x: table.cueball.pos.x, y: table.cueball.pos.y };
      const targetId = lowestOnTableBallId(table);
      const targetPos = targetId !== null ? ballPositionById(table, targetId) : null;
      if (targetPos) {
        const pockets = getPocketAims().map((p) => ({ x: p.x, y: p.y }));
        let chosenPocket: { x: number; y: number } | null = null;
        const resolved = fallbackTargetPocket(
          cuePos,
          { x: targetPos.x, y: targetPos.y },
          pockets,
        );
        if (resolved) {
          chosenPocket = { x: resolved.pos.x, y: resolved.pos.y };
        } else {
          // P3.3 フォールバック: target ball の最近接ポケット
          let bestDist = Infinity;
          for (const p of pockets) {
            const d = Math.hypot(p.x - targetPos.x, p.y - targetPos.y);
            if (d < bestDist) {
              bestDist = d;
              chosenPocket = { x: p.x, y: p.y };
            }
          }
        }
        if (chosenPocket) {
          lastApproachedPocket = chosenPocket;
          dispatchDirector({ type: 'prepare-pocket-cam', pocketPos: chosenPocket });
        }
      }
    }
    // F-31 Director: ショット発射通知（P3 §14 でカット禁止帯開始・inShot=true だけ立つ）。
    dispatchDirector({ type: 'shot-fired', isBreak: isBreakShotNow });
    // F-31 P3: framing-lost / foul-detected / first-contact の「1 ショット 1 回」フラグ初期化。
    framingLostStartWallMs = null;
    framingLostDispatchedThisShot = false;
    foulDispatchedThisShot = false;
    firstContactDispatchedThisShot = false;
    // 送信側（権威・遠隔のみ）: このショットの軌跡記録を開始（相手の補間再生用）。
    if (isRemoteNet() && netConnected && !spectating) {
      trackRecorder = createTrackRecorder();
      shotPlayMs = 0;
    }
  }

  // 狙撃カム中の解除イベント監視: ショット中・未 release・対象が生きている時のみ評価。
  //   - 的玉のポケットイン → 0.4 秒イージングで解除（最優先＝最重要の達成感）
  //   - 的玉のクッション接触 → 0.4 秒イージングで解除（ただし**ポケット近傍は除外**＝
  //     ポケット入り口のジョー〔ナックル〕接触が `cushion` イベントとして発火し、
  //     入る直前にビューが動き出す不具合を回避する。ポケット中心から距離 4R 以内の
  //     クッション接触は「ポケット入り口での弾き」とみなしフリーズを維持する）
  //   - 的玉ヒット直後の進路ずれ → 0.3 秒解除（**最初に動き出した1フレームのみ評価**＝
  //     その後カロム/コンビで方向が変わってポケットに入るケースを誤検知しない）
  // 解放後の方向だけ追従フレーミングは別ブロック（下方）で per-frame に適用するため、
  // ここでは「解放イベント」フラグを立てるだけ＝ rig 操作はしない。
  if (shotActive && shotFreezeCamActive && !shotFreezeReleased && shotFreezeTargetBallId !== null) {
    const tid = shotFreezeTargetBallId;
    // ポケット近傍判定の閾値（ポケット中心から的玉中心までの距離）。
    // 球径2個分（4R ≈ 0.13m）を「入り口エリア」とみなす。これより内側でのクッション接触は
    // ジョー（ナックル）接触の可能性が高く、解除トリガーとして使わない。
    const POCKET_PROXIMITY_M = 4 * ballRadius;
    // pot を最優先で評価（ポケットイン > クッション）。outcomes 同一フレームに両方ある時のため。
    // ポケットイン時は即時 releaseFreeze せず、視覚的に球が沈み終わるのを待つ「遅延」を仕込み、
    // 下方の per-frame カウントダウンで release を呼ぶ。
    for (const o of outcomes) {
      if (o.type === 'pot' && o.a === tid) {
        if (shotFreezePotReleaseDelay <= 0) {
          shotFreezePotReleaseDelay = SHOT_FREEZE_POT_RELEASE_DELAY;
        }
        break;
      }
    }
    // pot 遅延のカウントダウン。0 になった瞬間に release を発火（cinema 風の長めイージング）。
    if (shotFreezePotReleaseDelay > 0) {
      shotFreezePotReleaseDelay -= frame;
      if (shotFreezePotReleaseDelay <= 0) {
        shotFreezePotReleaseDelay = 0;
        rig.releaseFreeze(SHOT_FREEZE_POT_RELEASE_EASE);
        shotFreezeReleased = true;
      }
    }
    // 以降の cushion / clearly-missing / target-off-table 判定は **pot 遅延中は抑止**する。
    // 同一フレームに pot と cushion の両方が来た場合や、pot 後に球が消えて find が null を
    // 返す場合に、遅延を無視して即時解放してしまう不具合を回避する（pot 遅延が走っているなら
    // それを待つのが正しい挙動＝ポケットインが既に確定している）。
    if (!shotFreezeReleased && shotFreezePotReleaseDelay <= 0 && shotFreezePocketPos) {
      // クッション接触判定: ポケット中心から離れている時のみ解除。近傍ならジョー接触の
      // 可能性が高いのでフリーズ維持（次フレームで pot イベントが来る期待）。
      for (const o of outcomes) {
        if (o.type === 'cushion' && o.a === tid) {
          const tb = table.balls.find((b) => b.id === tid);
          if (tb) {
            const d = Math.hypot(
              tb.pos.x - shotFreezePocketPos.x,
              tb.pos.y - shotFreezePocketPos.y,
            );
            if (d > POCKET_PROXIMITY_M) {
              rig.releaseFreeze(0.4);
              shotFreezeReleased = true;
              break;
            }
            // 近傍のクッション/ナックル接触は無視（ポケット入り口扱い）
          } else {
            // 的玉が見つからない（落球済 等）: 安全側で解除
            rig.releaseFreeze(0.4);
            shotFreezeReleased = true;
            break;
          }
        }
      }
    }
    // 同様に「明らかに外れ」「的玉盤外」判定も pot 遅延中は抑止する。
    if (!shotFreezeReleased && shotFreezePotReleaseDelay <= 0 && shotFreezePocketPos) {
      const tb = table.balls.find((b) => b.id === tid);
      if (tb && tb.onTable()) {
        const vx = tb.vel.x;
        const vy = tb.vel.y;
        // 明らかに外れ判定は「最初に動き出した1フレームだけ」評価する。
        // 後段で他球やクッションに触れて方向が変わるケース（カロム/コンビでポケットに入る）の
        // 誤検知を防ぐ。判定済みフラグ `shotFreezeMissChecked` で多重評価を抑止。
        if (!shotFreezeMissChecked && Math.hypot(vx, vy) > 0.05) {
          shotFreezeMissChecked = true;
          const pos = { x: tb.pos.x, y: tb.pos.y };
          const vel = { x: vx, y: vy };
          if (isClearlyMissingPocket(pos, vel, shotFreezePocketPos)) {
            rig.releaseFreeze(0.3);
            shotFreezeReleased = true;
          }
        }
      } else {
        // 的玉が盤外（落球など outcomes 取りこぼし）: 即時短イージング解除（保険）。
        rig.releaseFreeze(0.3);
        shotFreezeReleased = true;
      }
    }
  }
  // 狙撃カム解放後の「次ショットのプレビュー」追従フレーミング（ユーザー要望 2026-06-18d）。
  //   要件: ズーム（radius）と仰角は変更せず、**手前→奥に「手玉→次の的玉→次球を入れるポケット」が
  //         並ぶ depth 構図**で手玉の動きを追う＝次ショットの狙撃ビューに相当する構図。
  //   実装:
  //     ①次の的玉 = `lowestOnTableBallId(table)` で算出。
  //     ②**次球のポケット P** = `fallbackTargetPocket(cue, next, pockets)`＝手玉→次球の延長線で
  //       最近接の前方ポケット（自動照準 OFF 時のフォールバックと同じロジックを再利用）。
  //     ③`computePostFreezeFraming(cue, P)` で lookAt=手玉・azimuth=cue から P と逆方向 を返す。
  //       camera は P と反対側（cue 後方）に置かれ、cue → 次球 → P が perspective 奥行きに並ぶ。
  //     ④azimuth は **main.ts 側の slow lerp**（時定数 0.1≈1秒で90%収束）で `postFreezeAzimuth` を
  //       目標方位へ寄せ、その値を `alignToAim(postFreezeAzimuth - π)` に渡す。rig 内部の高速 lerp が
  //       postFreezeAzimuth を即追従するため有効回転速度は slow lerp で決まる（≈90°/秒）。
  //   注意: setOrbitTarget は内部で aligning=false を立てるため、必ず alignToAim を後に呼ぶ。
  //         次球または次球ポケットが特定できない場合は lookAt だけ手玉に寄せて方位は維持する。
  if (shotActive && shotFreezeCamActive && shotFreezeReleased) {
    const cb = table.cueball;
    if (cb.onTable()) {
      const nextId = lowestOnTableBallId(table);
      const nb =
        nextId !== null
          ? table.balls.find((b) => b.id === nextId && b.onTable())
          : null;
      if (nb) {
        const cuePos2 = { x: cb.pos.x, y: cb.pos.y };
        const nextPos = { x: nb.pos.x, y: nb.pos.y };
        const pocketsRaw = getPocketAims().map((p) => ({ x: p.x, y: p.y }));
        const resolved = fallbackTargetPocket(cuePos2, nextPos, pocketsRaw);
        const framing = computePostFreezeFraming(
          cuePos2,
          resolved ? resolved.pos : null,
        );
        rig.setOrbitTarget(framing.lookAt.x, framing.lookAt.y, 0);
        if (framing.azimuth !== null) {
          // 自前の slow lerp で「真の目標方位」へゆっくり寄せる。最初のフレームは現在 azimuth から開始。
          if (postFreezeAzimuth === null) postFreezeAzimuth = rig.getAzimuth();
          const c = 1 - Math.pow(POST_FREEZE_AZIMUTH_LERP_BASE, frame);
          postFreezeAzimuth = lerpAngle(postFreezeAzimuth, framing.azimuth, c);
          // alignToAim は内部で +π を加える（手球後方からショット方向を見るための慣習）。
          // 我々は desiredAzimuth = postFreezeAzimuth が欲しいので angle = postFreezeAzimuth - π を渡す。
          rig.alignToAim(postFreezeAzimuth - Math.PI);
        }
      } else {
        // 次の的玉なし: 手玉だけを lookAt に。方位は維持（alignToAim は呼ばない）。
        rig.setOrbitTarget(cb.pos.x, cb.pos.y, 0);
      }
    }
  }

  // 近接静止: 全球が極低速のまま REST_HOLD 秒続けば強制静止（ゼロ速度付近の
  // 数値リミットサイクルで allStationary に収束しないケースを見た目どおり止める）。
  // 最終保険として SETTLE_TIMEOUT も併用。次フレームで通常の終了処理が走る。
  if (!remotePlayActive && shotActive && !resting) {
    shotElapsed += frame;
    nearRestElapsed = restActivity(table) < REST_SPEED_EPS ? nearRestElapsed + frame : 0;
    if (nearRestElapsed > REST_HOLD || shotElapsed > SETTLE_TIMEOUT) {
      forceSettle(table);
    }
  }
  // 全球静止: ショット終了。俯瞰/自動トップビューを解除し、スクラッチ復帰＆最若球へ自動エイム。
  // 弱いショットが1フレーム内で発射〜静止しても、上の発射→ここで確実に終了処理される。
  if (!remotePlayActive && shotActive && resting) {
    // F-31 Director: ショット終了（全球停止）を通知 → Director が次の構図（Player POV 等）へ復帰。
    dispatchDirector({ type: 'all-settled' });
    lastApproachedPocket = null; // 次ショットの aimedPocket は fallbackTargetPocket で再算出
    shotActive = false;
    // CPU ショットフラグ: 後段のブレイクラン判定ガードで参照するため、解除前に退避する。
    // （以前は先に false 代入していたためガードが常に素通りし、CPU のミスショットで
    //  ユーザーの連続ブレイクラン記録（localStorage 永続）が破壊されていた）
    const wasCpuShot = shotByCpu;
    // CPU ショットフラグを解除（次の構えからはユーザー設定に従う）。
    shotByCpu = false;
    // 次の構えへユーザー操作仰角を引き継がない。実際のリセットは次の aimReady 立ち上がり時に
    // controlsApi.resetCueElevation() で行う（B 案＝静止直後にキューが動いて見えるのを避ける）。
    elevationResetArmed = true;
    // 狙撃カム: 静止までに release されなければここで保険解除（短イージング）。
    // 既に release 済みでも freezeReleaseBlend は内部で 0 へ収束する。
    if (shotFreezeCamActive) {
      if (!shotFreezeReleased) rig.releaseFreeze(0.2);
      shotFreezeCamActive = false;
      shotFreezeTargetBallId = null;
      shotFreezePocketPos = null;
      shotFreezeReleased = true;
      // pot 遅延・方位 slow lerp 状態も完全リセット。
      shotFreezePotReleaseDelay = 0;
      postFreezeAzimuth = null;
      // 解放後フレーミングで設定した orbitOverride/aligning を解除し、通常の追従（手玉中心）へ戻す。
      rig.clearOrbitTarget();
    }
    rig.setOverview(false);
    rig.setFollow(false); // フォローフレーミング解除
    rig.setAutoTopView(false); // 自動トップビューは停止で解除（手動ロックは維持）
    rig.clearUserOverride();
    // ポケット内で止まった球（強制静止/数値誤差で落ちなかった球）を確実にポケットイン化。
    dropPottedBalls(table);

    if (replayingRemote) {
      // 相手ショットのローカル再生が静止。権威確定が来ていれば補正、未着なら待つ。
      if (pendingAuthoritative) {
        applyAuthoritative(pendingAuthoritative.state, pendingAuthoritative.balls);
      } else {
        awaitingAuthoritative = true;
      }
    } else if (rulesActive && preShot) {
      // ルール判定（9ボール）: 観測を作り純粋 reducer へ。結果で盤面・配置・HUD を駆動。
      const obs = endShotObservation(
        table,
        preShot,
        shotOutcomes,
        pendingDoubleHit,
        breakHeadStringIds,
      );
      preShot = null;
      shotOutcomes = [];
      // 3ポイントルール設定を反映（設定変更を即時に取り込む）。
      ruleParams = { ...ruleParams, breakThreePoint: isThreePointBreak() };
      try {
        const out = dispatch(ruleState, { type: 'shot', obs }, ruleParams);
        ruleState = out.state;
        lastRuleResult = out.result;
        applyRuleResult(out.result);
      } catch (err) {
        console.error('ルール判定に失敗:', err);
      }
      // F-31 P2b §12.2: ファウル検出 → Beauty Wide でリセット感を演出。
      // all-settled の player-pov 復帰を上書きする（MIN_HOLD バイパス・1 ショット 1 回）。
      // foul 判定は scratch（手玉ポケットイン）を含む。
      if (
        !foulDispatchedThisShot &&
        getBroadcastMode() === 'director' &&
        lastRuleResult?.foul
      ) {
        dispatchDirector({ type: 'foul-detected' });
        foulDispatchedThisShot = true;
      }
      // ブレイクラン判定（9ボールルールパス）。
      // 観戦中 / CPU ショット中はカウント対象外（自分のショットのみ）。
      if (!spectating && !wasCpuShot) {
        const numberedPotted = obs.pottedIds.filter((id) => id !== 0).length;
        const cleared = objectBallsRemaining(table) === 0;
        breakRun.recordShot(numberedPotted, obs.cueScratched, cleared);
      }
      // 個人分析: ShotRecord を組み立ててサーバーへ送信。
      // serverStats=true（ログイン済み）のみ送信＝未認証は集計を作らない（成りすまし防止）。
      // 観戦中は自分のショットではないため記録しない（撞いた人のみ送る）。
      // **対戦中のみ集計**（netConnected=true）。ソロはオートエイム等のガイド機能で
      // 信憑性が落ちるため、プロファイル統計からは除外する（送信自体スキップ）。
      // CPU 戦は集計対象外（cpuMatch=true でスキップ）。
      if (analyticsCtx && serverStats && netConnected && !spectating && !cpuMatch && lastRuleResult) {
        try {
          const nextTargetId = lowestOnTableBallId(table);
          const ana = finalizeAnalytics(
            table,
            analyticsCtx,
            obs.pottedIds.filter((id) => id !== 0),
            nextTargetId,
          );
          const isBreakShot = analyticsPhaseBefore === 'breakPlacement';
          const legalBreak = isBreakShot ? !lastRuleResult.foul : null;
          const numberedPotted = obs.pottedIds.filter((id) => id !== 0);
          const rec: ShotRecord = {
            ts: Date.now(),
            mode: 'nineBall',
            online: netConnected,
            isBreak: isBreakShot,
            legalBreak,
            pottedCount: numberedPotted.length,
            scratched: obs.cueScratched,
            fouls: lastRuleResult.foulReasons.slice(),
            noRail: lastRuleResult.foulReasons.includes('noRail'),
            pocketPottedIds: ana.pocketPottedIds,
            cuePathLengthM: ana.cuePathLengthM,
            nextLeaveAngleDeg: ana.nextLeaveAngleDeg,
            nextLeaveDistanceM: ana.nextLeaveDistanceM,
            rackWin:
              ruleState.phase === 'gameOver'
                ? ruleState.winner !== null && ruleState.winner === myPlayer
                : null,
          };
          lobby.reportShot(nickname, rec);
        } catch (err) {
          console.error('shotRecord 送信失敗:', err);
        }
      }
      analyticsCtx = null;
      analyticsPhaseBefore = null;
      // オンライン: 権威として確定結果（状態＋盤面）を送信。相手は再生をこれで補正。
      if (netConnected && !spectating) {
        // 残りの軌跡サンプルを送り切ってから権威確定を送る（順序保証）。
        if (trackRecorder) {
          const tail = trackRecorder.drain();
          if (tail.length > 0) transport?.send({ t: 'track', s: tail });
          trackRecorder = null;
        }
        transport?.send({
          t: 'shotEnd',
          state: ruleState,
          balls: serializeBalls(table),
        });
      }
      trackRecorder = null;
      syncRuleHud();
      maybeRecordResult();
    } else {
      // ドリル（プラクティス基盤・ルール無効）: 観測を取り judgeDrillShot で合否判定する。
      if (currentDrill && preShot) {
        const dobs = endShotObservation(
          table,
          preShot,
          shotOutcomes,
          pendingDoubleHit,
          breakHeadStringIds,
        );
        preShot = null;
        shotOutcomes = [];
        handleDrillShot(dobs);
      }
      // 非ルールモード（プラクティス / 10・8ボール）: 従来挙動。
      const scratched = isCueBallScratched(table);
      // ブレイクラン判定（プラクティス以外）。再ラックで盤面が戻る前に評価する。
      if (playMode !== 'practice') {
        const objAfter = objectBallsRemaining(table);
        const potted = objBallsAtFire - objAfter;
        breakRun.recordShot(potted, scratched, objAfter === 0);
      }
      if (scratched) {
        respotCueBall(table); // 手球を盤上へ戻す（位置は仮。以後ボールインハンドで自由配置）
      }
      if (lowestOnTableBallId(table) === null && playMode !== 'practice') {
        // 全的球ポケットイン: 再ラックして最若球へ照準（リセットと同じ挙動）。
        resetRackAndAim(playMode);
      } else if (table.cueball.onTable()) {
        // 通常静止・スクラッチ復帰後: 最若球へ自動エイム。
        if (scratched) {
          // スクラッチ復帰: 手玉を中心に据えてボールインハンド（自由配置）。
          frameCueBall();
          controlsApi.enterBallInHand();
        } else {
          aimAtLowestBall();
          rig.alignToAim(cue.aim.angle);
        }
      }
    }
    // ショット確定後の静止盤面を保存（レジューム用）。次回起動でこの局面から再開できる。
    persist();
  }

  // ショット後、ボールが動き始めて静止に戻ったタイミングで hittingAnimation を
  // リセットする。これをしないと strokeZ が累積し、次の構え表示でキュー mesh が
  // 手球より上に大きく持ち上がったままになる（不具合）。
  if (resting && cue.hittingAnimation) {
    cue.hittingAnimation = false;
    cue.t = 0;
    cue.hitAnimationWeight = 0;
  }

  // キュー mesh の中心は手球から「ショット反対方向」へ length/2 だけ離れた点。
  // カメラがその中心点に極端に近い（cue.length × 0.15 ≈ 13cm 以内）ときだけ非表示。
  // 以前の閾値 0.45×length は緩すぎ、通常プレイ視点でもキューが消えていた（不具合）。
  // レール貫通（キューがテーブル枠外を通る）は別問題で Phase 3 の cueintersect で対応する。
  const half = cue.length * 0.5;
  cueMidTmp.set(
    cb.x - Math.cos(cue.aim.angle) * half,
    cb.y - Math.sin(cue.aim.angle) * half,
    cb.z,
  );
  const camCueDist = ctx.camera.position.distanceTo(cueMidTmp);
  const cueOccludesCamera = camCueDist < cue.length * 0.15;

  // オンライン・自分の手番: 照準（角度/撞点/仰角/強さ/手球位置）を相手へ高頻度同期。
  // 相手はこれでキューを描画する（待ち側でも相手の構えが見える）。
  if (
    netConnected &&
    !spectating &&
    ruleState.current === myPlayer &&
    resting &&
    !replayingRemote
  ) {
    const p = currentShotPayload();
    const sig =
      `${p.angle.toFixed(4)},${p.offsetX.toFixed(3)},${p.offsetY.toFixed(3)},` +
      `${p.elevation.toFixed(4)},${p.power.toFixed(3)},${p.cueX.toFixed(4)},${p.cueY.toFixed(4)}`;
    const tnow = performance.now();
    if (sig !== lastAimSig && tnow - lastAimSentMs > 60) {
      lastAimSig = sig;
      lastAimSentMs = tnow;
      transport?.send({ t: 'aim', shot: p });
    }
  }

  // 対戦中はガイド（ショット力/予測軌跡）を一律 OFF。
  // ただし Magician（skill effect showAimGuideInMatch）装備時は対戦中でも予測軌跡ガイドを表示する。
  // ★ドリル中（currentDrill）は自動照準・全ガイド・スキル補助を一切禁止（スキル測定のため）。
  const drillActive = currentDrill !== null;
  const powerOn = isPowerGuide() && !netConnected && !drillActive;
  const magicianGuideOn =
    !drillActive &&
    netConnected &&
    inventory.effectiveSkillEffects().showAimGuideInMatch === true;
  const reflectOn = ((isReflectGuide() && !netConnected) || magicianGuideOn) && !drillActive;

  // 手球が盤上で静止している時に構え表示（観戦側も相手のキューを表示）。
  // 相手ショットの座標ストリーム表示中はキューを出さない（撞いている最中）。
  const aimReady = resting && table.cueball.onTable() && !replayingRemote;
  // 次の構え立ち上がり（aimReady: false→true）で予約済みの仰角リセットを発火する。
  // ショット静止直後ではなく構え遷移時に呼ぶことで、視覚的に滑らかにユーザー仰角を 0 へ戻す。
  if (aimReady && !prevAimReady && elevationResetArmed) {
    controlsApi.resetCueElevation();
    // ショット後の構え立ち上がり: カメラのズーム/手動オービットもデフォルトへ戻す。
    // 直前ショット中にユーザーが拡大/視点変更した状態を次ショットへ引き継がない。
    // 結果として「手玉・的玉・狙うべきポケット」が見える初期半径で構えに入れる。
    // 狙撃カムの ON/OFF 切替も次のラック/構え立ち上がりで反映される（computeAspectRadius が
    // 設定を毎回参照するため）。`setInitialRadius` も同時更新して `rig.reset()` の戻り先を
    // 最新設定に合わせる（resetRackAndAim 経由のフルリセット後も拡大状態で構えられる）。
    const nextRadius = computeAspectRadius();
    rig.clearUserOverride();
    rig.setRadius(nextRadius);
    rig.setInitialRadius(nextRadius);
    elevationResetArmed = false;
  }
  // 「対象球が隠れたら自動トップビュー」: 構えが立ち上がった瞬間に判定。
  // 次に狙うべき的玉（最若球）への直線上に他球がある＝隠れている時は真上ビューへ。
  // ショットで撞いた瞬間に解除される（onShoot で setTopViewLocked(false)）。
  // source='auto' でロック種別を区別＝HUDの自動半透明スキップ判定（rig.isAutoTopActive）に使われる。
  // 手動トップビューボタン由来（source='manual'）のロックは従来通り透過する。
  if (aimReady && !prevAimReady && isAutoTopOnObscured()) {
    const obscured = isTargetObscured();
    rig.setTopViewLocked(obscured, 'auto');
  }
  // 構え立ち上がりタイミングで対戦勧誘の発火判定（CPU 戦中の静止／待機モード中のソロプレイ静止）。
  // roster 更新だけでは「球が動いている時に新規 waiting が来た→静止後に通知」が漏れるため、
  // 構え立ち上がり時にも評価する（pickMatchInvitationCandidate が条件を満たさなければ no-op）。
  if (aimReady && !prevAimReady) {
    maybeShowMatchInvitation();
  }
  prevAimReady = aimReady;
  const showCue = aimReady && !cueOccludesCamera;
  cue.aim.pos.copy(cb);
  cue.mesh.visible = showCue;
  cue.shadowMesh.visible = showCue;
  // 予測軌跡ガイドON時は、テーブルを突き抜ける固定長の照準ヘルパーは冗長なので隠す。
  cue.showHelper(aimReady && !reflectOn);
  cue.update(frame);
  cueGlimmer.update(frame); // オートエイム補正時のきらめきフェードを進める

  // STOPショット相当の引き量を通過した瞬間にキューをきらりと一閃させる（旧ショット力ガイドの
  // 「緑＝STOP」フィードバックの代替）。判定: 的玉に届きかつ衝突時点でも滑走中（slideDist >= contact）。
  // チラつき防止のため 1 構え（パワー>0 の連続区間）あたり 1 回のみ発火。パワー=0 で再アーム。
  if (aimReady && cue.aim.power > 0) {
    const predStop = shotPrediction(cue.aim.power, cue.aim.elevation);
    const contactStop = firstContactDistance(table, cue.aim.angle);
    const isStopNow =
      contactStop !== null &&
      predStop.reach >= contactStop &&
      predStop.slideDist >= contactStop;
    if (isStopNow && !stopShotPrevIsStop && stopShotGlimmerArmed) {
      cueGlimmer.trigger();
      stopShotGlimmerArmed = false; // 1 構えで 1 回のみ
    }
    stopShotPrevIsStop = isStopNow;
  } else {
    // パワー=0（指離し）: 次のパワートラックで再びきらめける状態に戻す。
    stopShotPrevIsStop = false;
    stopShotGlimmerArmed = true;
  }

  // ショット力インジケーター（設定ON & 構え中のみ）。予測軌跡ガイドON時は重複するため出さない。
  // 赤=的玉に届かない / 黄=届く（衝突時は転がり=押し）/ 緑=衝突時も滑走中（STOP/スタン）。
  // 設定は廃止されたため通常は非表示（`isPowerGuide()` が常に false）。ロジック自体は将来再有効化
  // できるよう残置する（コードを削除すると `shotGuide` インスタンスごと巻き戻る必要があるため）。
  if (powerOn && !reflectOn && aimReady) {
    const pred = shotPrediction(cue.aim.power, cue.aim.elevation);
    const contact = firstContactDistance(table, cue.aim.angle);
    let length: number;
    let color: number;
    if (contact === null) {
      // 的玉なし: 色は付けず（中立色）到達距離だけを表示（上限でクランプ）。
      length = Math.min(pred.reach, GUIDE_MAX_LEN);
      color = GUIDE_NEUTRAL;
    } else if (pred.reach < contact) {
      // 届かない: 到達距離まで赤。
      length = pred.reach;
      color = GUIDE_RED;
    } else {
      // 届く: 的玉までを塗る。衝突時も滑走中なら緑(STOP)、転がりなら黄。
      length = contact;
      color = pred.slideDist >= contact ? GUIDE_GREEN : GUIDE_YELLOW;
    }
    shotGuide.update({
      originX: cb.x,
      originY: cb.y,
      z: -ballRadius * 0.98, // クロス直上に薄く敷く
      angle: cue.aim.angle,
      length,
      color,
    });
  } else {
    shotGuide.hide();
  }

  // ドラッグ位置ガイド（設定ON & 構え中・トップビュー以外のみ）。
  // canvas 上の grabKind 判定（手玉中心↔キュー手元 / 手玉中心↔前方ガイド の各 44px 圏）を
  // screen-space で可視化する。レール際で screen-space 線分が短くなれば帯も自然に狭く見える。
  // トップビューでは仰角/aim ドラッグ自体が機能しないため非表示（パン/ピンチが優先）。
  // aimReady は内部で `!replayingRemote && resting && cueball.onTable()` を含むため、
  // ここでは追加条件「設定ON」「トップビューでない」だけで十分。
  const showDragGuide =
    aimReady && isDragGuideEnabled() && !rig.isTopView();
  dragGuide.update({
    camera: ctx.camera,
    ballX: cb.x,
    ballY: cb.y,
    ballRadius,
    aimAngle: cue.aim.angle,
    elevation: cue.aim.elevation,
    cueLength: cue.length,
    forwardLen: AIM_FORWARD_LEN_M, // controls.ts grabKind の前方端と完全同期（2.5m）
    visible: showDragGuide,
    // 操作中のガイドのみ表示するため、現在のジェスチャを controls から取得して渡す。
    activeMode: controlsApi.getDragGesture(),
  });

  // 予測軌跡（設定ON & 構え中のみ）。撞点・強さ・仰角を反映した実シミュレート。
  // パワー未設定時は既定プレビュー強さ（≈4クッション）。aim が変化した時だけ再計算。
  if (reflectOn && aimReady) {
    const previewPower =
      cue.aim.power > 0 ? cue.aim.power : DEFAULT_PREVIEW_POWER;
    const o = cue.aim.offset;
    const squirt = getSquirt();
    const sig =
      `${cb.x.toFixed(4)},${cb.y.toFixed(4)},${cue.aim.angle.toFixed(4)},` +
      `${previewPower.toFixed(3)},${o.x.toFixed(3)},${o.y.toFixed(3)},${cue.aim.elevation.toFixed(4)},${squirt.toFixed(3)}`;
    if (sig !== lastReflectSig) {
      lastReflectSig = sig;
      reflectPath = simulateCuePath(
        table,
        cue.aim.angle,
        previewPower,
        o,
        cue.aim.elevation,
        { maxPoints: REFLECT_MAX_POINTS, squirt },
      );
    }
    reflectGuide.update(reflectPath, -ballRadius * 0.95);
  } else {
    reflectGuide.hide();
    lastReflectSig = '';
  }

  // カット角ガイド: 設定ON & 構え中（静止）に、controls 内部で計算した直角四辺形 (4 頂点)を
  // 更新する。ターゲット球・ポケット解が無い場合は controls が null を返すのでガイドを隠す。
  if (isCutAngleGuide() && aimReady && !drillActive) {
    const verts = controlsApi.getCutAngleFrameVertices();
    if (verts) cutAngleFrame.update(verts);
    else cutAngleFrame.hide();
  } else {
    cutAngleFrame.hide();
  }

  // ポケット候補グロー: 自動照準が「狙える」と判定したポケットを緑半透明で表示（タップ切替用）。
  // 構え中（aimReady）のみ表示。controls 側が autoAim/aimTargetId/静止 を加味して空配列を返す。
  if (aimReady) {
    const mp = controlsApi.getMakeablePockets();
    pocketGlows.update(mp.positions, mp.selectedIndex);
  } else {
    pocketGlows.hide();
  }

  // ショット中（非自動トップ）はフォローフレーミングへ収める球を毎フレーム供給。
  // primary（手球・的球・ポケットへ向かう球）を必ず画面に収め、極端な引きを防ぐ。
  if (shotActive && !useAutoTopForShot()) {
    const ff = followFrame(table, followTargetId);
    rig.setFollowFrame(ff.primary, ff.secondary);
  }
  rig.setAimTarget(cb.x, cb.y, cb.z);
  rig.update(frame);

  // 球速HUD: ブレイクショット または プラクティスモード時は、その一打の瞬間最高速度を静止まで
  // 保持表示する。プラクティスでは練習目的で全ショットの球速を確認できるようにする。
  const showSpeedHud = shotActive && (shotIsBreak || getPlayMode() === 'practice');
  if (showSpeedHud) {
    breakPeakSpeed = Math.max(breakPeakSpeed, cueBallSpeed(table));
    controlsApi.setBallSpeed(breakPeakSpeed);
  } else if (!shotActive) {
    controlsApi.setBallSpeed(null); // 静止したら消す（次のショットまで非表示）
  }

  syncBallViews(snapshot(table), views, frame);
  // 配置モード中の「移動可能な球」をハロでふんわり明滅させる（手球/的玉の配置を視覚化）。
  // performance.now() を秒換算した時刻で位相を進める。配置可能でない時は内部で全ハロを隠す。
  updateHaloPulse(views, controlsApi.getMovableBallIds(), performance.now() / 1000);
  // 静止中の的玉数を保持（ショット間の安定値＝次ショットの基準）。
  // ショット中（resting=false）は更新しないので発射前の値が保たれる。
  if (resting) objBallsAtRest = objectBallsRemaining(table);
  ctx.renderer.render(ctx.scene, ctx.camera);
  // Spike A: メイン描画の直後に 2 パス目（画面右下の正方形）として描く。
  // 描画後はウィジェット側でフル画面ビューポートへ復帰する（次フレームのメイン描画を保護）。
  if (cueSpinWidget) cueSpinWidget.render(ctx.renderer, window.innerWidth, window.innerHeight);
};
animate();
